import React, { useState, useEffect, useMemo, useCallback, memo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Pencil, Check, X, Reply, Mic, Square, Loader2, ChevronRight, Wallet, FileDown, Eye, Download, Trash2, AlertTriangle, Zap } from 'lucide-react';
import ChatErrorNotice from './ChatErrorNotice';
import useDocumentBlobUrl from '../hooks/useDocumentBlobUrl';
import useVoiceRecorder from '../hooks/useVoiceRecorder';
import VoiceClipPlayer from './VoiceClipPlayer';
import { documentService } from '../services/documentService';
import { expenseService } from '../services/expenseService';
import apiService from '../services/apiService';
import { getAuthToken } from '../utils/authToken';
import { normalizeTexSymbols } from '../utils/mathSymbols';
import { splitByMatches } from '../utils/accentFold';
import { useNavigate } from 'react-router-dom';
import CodeBlock from './CodeBlock';
import AttachmentPill from './AttachmentPill';
import MessageAttachment from './MessageAttachment';
import DocumentPreviewModal from './DocumentPreviewModal';
import VersionHistoryDialog from './FileManager/VersionHistoryDialog';
import ContextViewerModal from './ContextViewerModal';
import EnergyReceiptModal from './energy/EnergyReceiptModal';
import CreatedDocumentModal from './CreatedDocumentModal';
import CalendarBookingCard from './CalendarBookingCard';
import CalendarPackageCard from './CalendarPackageCard';
import GuestPackageInvitationCard from './GuestPackageInvitationCard';
import ThinkingSteps from './ThinkingSteps';
import ResolutionCard from './cards/ResolutionCard';
import {
    documentDeleteConfig,
    expenseDeleteConfig,
    calendarDeleteConfig,
    calendarEventsDeleteConfig,
    noteDeleteConfig,
    taskDeleteConfig,
    noteShareConfig,
    reminderDeleteConfig,
    commentDeleteConfig,
    ticketBatchUpdateConfig,
    ticketBatchDeleteConfig,
    boardBatchDeleteConfig,
    boardLeaveConfig,
    columnMoveConfig,
    labelDeleteConfig,
    columnDeleteConfig,
    ticketAttachConfig,
    documentRenameConfig,
    bookDeleteConfig,
    bookRenameConfig,
    ruleChangeConfig,
    contactCreationConfig,
    eventInviteConfig,
    teamCreateConfig,
    teamShareConfig,
    teamDeleteConfig,
    directSendConfig,
    actionRevokeConfig,
    ruleAddConfig,
} from './cards/cardConfigs';
import { useLanguage } from '../i18n';
import { userErrorMessage } from '../utils/userError';
import { isDisclaimerText, hastText, separateDisclaimerLines } from '../utils/disclaimerBlock';
import { cardEntriesForMessage } from '../utils/cardResolutionPatch';

// Renders the `expenses_listed` tool fanout as a vertical list of
// interactive transaction chips directly in the chat stream.
// Clicking any row deep-links into the Focus Overlay (spec: "Natural
// Language Retrieval — UI Injection").
// Side-by-side duplicate-detection card. Renders when the create_expense
// tool returned a duplicate_warning SSE event — shows the existing
// transaction(s) next to the candidate so the user can decide whether to
// proceed anyway (reply "proceed" / "yes" in chat) or skip the upload.
// The actual receipt image (or PDF page-1 render) for visual duplicate
// comparison. Top-level so the blob-URL hook has a stable identity.
const ReceiptThumb = ({ documentId, label }) => {
    const { t } = useLanguage();
    const { url } = useDocumentBlobUrl(documentId, !!documentId, { path: 'thumbnail' });
    if (!url) return null;
    return (
        <img
            src={url}
            alt={label || t('msgReceiptImageAlt', 'Receipt image')}
            className="w-full max-h-28 object-contain rounded border border-pai-subtle bg-white"
        />
    );
};

const fmtAmt = (amount, currency = 'USD') => {
    if (amount == null) return '—';
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
    } catch (_) { return `${currency} ${Number(amount).toFixed(2)}`; }
};

// Visual verification for batch duplicates (Image preview ticket): the KEPT copy
// and the redundant DUPLICATE shown side-by-side so the user can confirm they're
// identical in a glance before deleting anything. Module-level so the ReceiptThumb
// blob images stay mounted (no flicker — same lesson as the Disco Night fix).
const DuplicatePreviewPair = ({ original, duplicate, merchant, amount, currency }) => {
    const { t } = useLanguage();
    if (!duplicate) return null;
    return (
        <div className="rounded border border-[rgba(245,165,36,0.28)] bg-[rgba(245,165,36,0.06)] p-2">
            <div className="text-[10px] text-pai-meta mb-1 truncate">
                {merchant || t('msgReceiptFallback', 'Receipt')} · {fmtAmt(amount, currency)}
            </div>
            <div className="flex items-stretch gap-2">
                <figure className="flex-1 min-w-0 m-0">
                    {original ? <ReceiptThumb documentId={original} label={t('msgKeptCopyLabel', 'Kept copy')} /> : null}
                    <figcaption className="mt-0.5 text-[10px] text-center text-emerald-700 dark:text-emerald-400 font-medium">{t('msgKeptLabel', 'Kept')}</figcaption>
                </figure>
                <div className="flex items-center text-pai-faint text-xs font-bold px-0.5">=</div>
                <figure className="flex-1 min-w-0 m-0">
                    <ReceiptThumb documentId={duplicate} label={t('msgDuplicateLabel', 'Duplicate')} />
                    <figcaption className="mt-0.5 text-[10px] text-center text-rose-700 dark:text-rose-400 font-medium">{t('msgDuplicateLabel', 'Duplicate')}</figcaption>
                </figure>
            </div>
        </div>
    );
};

// One side of the duplicate comparison. Defined at MODULE level (NOT inside
// DuplicateWarningCard) so its component identity is stable across re-renders —
// otherwise every parent re-render (e.g. the per-second audio-recording timer or
// a mouse-over) recreated this function, React remounted the whole row, and the
// ReceiptThumb blob image was re-fetched and flashed in/out ("Disco Night" bug).
const DuplicateRow = ({ row, label, accent = 'amber', onNavigate, onPreview, onDownload }) => {
    const { t } = useLanguage();
    const isIncome = (row?.direction || 'expense') === 'income';
    const href = row?.id ? `/expenses?focus=${row.id}&book=${row.book_id || 'unassigned'}&overlay=1` : null;
    const accentBox = accent === 'amber'
        ? 'border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/15'
        : 'border-rose-300 dark:border-rose-700 bg-rose-50/60 dark:bg-rose-900/15';
    const accentLabel = accent === 'amber'
        ? 'text-amber-800 dark:text-amber-300'
        : 'text-rose-800 dark:text-rose-300';
    return (
        <div className="flex-1 min-w-0 space-y-1">
            <div
                onClick={href ? () => onNavigate(href) : undefined}
                className={`rounded-lg border ${accentBox} p-2 ${href ? 'cursor-pointer hover:bg-white/[0.06]' : ''}`}
            >
                <div className={`text-[10px] uppercase font-semibold tracking-wide ${accentLabel}`}>{label}</div>
                <div className="mt-1 flex items-baseline gap-2">
                    <span className={`text-sm font-bold ${isIncome ? 'text-emerald-700 dark:text-emerald-300' : 'text-white'}`}>
                        {isIncome ? '+' : ''}{fmtAmt(row.amount, row.currency)}
                    </span>
                    <span className="text-xs text-pai-secondary truncate">
                        {row.merchant || '—'}
                    </span>
                </div>
                <div className="mt-1 text-[10px] text-pai-meta">
                    {row.expense_date || '—'}
                    {row.book_name && ` · ${row.book_name}`}
                </div>
            </div>
            {row?.document_id && (
                <div className="space-y-1">
                    {/* Ticket Image — visual side-by-side comparison */}
                    <ReceiptThumb documentId={row.document_id} label={t('msgReceiptImageWithName', '{name} image', { name: row.merchant || t('msgReceiptFallback', 'Receipt') })} />
                    <div className="flex items-center gap-1">
                        {onPreview && (
                            <button
                                type="button"
                                onClick={() => onPreview(row.document_id, `${row.merchant || t('msgReceiptFallback', 'Receipt')}`)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] text-pai-secondary bg-white/[0.04] border border-pai hover:bg-white/[0.07] rounded"
                                title={t('msgPreviewReceipt', 'Preview receipt')}
                            >
                                <Eye size={11} /> {t('cardPreview', 'Preview')}
                            </button>
                        )}
                        {onDownload && (
                            <button
                                type="button"
                                onClick={() => onDownload(row.document_id, `${row.merchant || t('msgReceiptFallback', 'Receipt')}`)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] text-pai-secondary bg-white/[0.04] border border-pai hover:bg-white/[0.07] rounded"
                                title={t('msgDownloadReceipt', 'Download receipt')}
                            >
                                <Download size={11} /> {t('cardDownload', 'Download')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const DuplicateWarningCard = ({ payload, onNavigate, onPreview, onDownload }) => {
    const { t } = useLanguage();
    const candidate = payload?.candidate || {};
    const existing = payload?.existing || [];
    // Two flavours: an upload duplicate (create_expense) vs. a book-to-book
    // transfer duplicate (assign_expense_to_book) — the latter carries
    // context: 'transfer' and a target_book_name, and gets interactive
    // one-click resolution buttons (Doppelgänger resolution ticket).
    const isTransfer = payload?.context === 'transfer';
    const targetBook = payload?.target_book_name || t('msgThatBookFallback', 'that book');
    const sourceBook = candidate?.book_name || t('msgUnassignedFallback', 'Unassigned');
    // Interactive conflict-resolution state (transfer flavor). Hooks must run
    // before the early return below, so they live up here.
    const [resolution, setResolution] = useState(null); // resolved status line
    const [busy, setBusy] = useState(null);             // 'move' | 'delete' while in-flight
    const [error, setError] = useState(null);
    if (!existing.length) return null;

    // One-click resolutions hit the expense REST endpoints directly (no chat
    // round-trip) and broadcast so live pages (ExpensesPanel/CalendarPanel) refresh.
    const moveAnyway = async () => {
        if (busy || resolution || !candidate.id) return;
        setBusy('move'); setError(null);
        try {
            await expenseService.updateExpense(candidate.id, { book_name: targetBook });
            window.dispatchEvent(new CustomEvent('omnis:expense-updated', { detail: { id: candidate.id } }));
            setResolution(t('msgMovedIntoAnyway', 'Moved into {target} anyway — a second copy now lives there.', { target: targetBook }));
        } catch (_) {
            setError(t('msgCouldNotMoveTransaction', 'Could not move the transaction. Please try again.'));
        } finally { setBusy(null); }
    };
    const deleteFromSource = async () => {
        if (busy || resolution || !candidate.id) return;
        setBusy('delete'); setError(null);
        try {
            await expenseService.deleteExpense(candidate.id);
            window.dispatchEvent(new CustomEvent('omnis:expense-deleted', { detail: { id: candidate.id } }));
            window.dispatchEvent(new CustomEvent('omnis:expense-updated', { detail: { id: candidate.id } }));
            setResolution(t('msgDeletedDuplicateFrom', 'Deleted the duplicate from {source}. {target} keeps the original.', { source: sourceBook, target: targetBook }));
        } catch (_) {
            setError(t('msgCouldNotDeleteDuplicate', 'Could not delete the duplicate. Please try again.'));
        } finally { setBusy(null); }
    };
    const keepInSource = () => {
        if (busy || resolution) return;
        setResolution(t('msgKeptDuplicateIn', 'Kept the duplicate in {source}. Nothing was moved.', { source: sourceBook }));
    };
    // Upload-duplicate (non-transfer) one-click resolutions — let the user decide
    // by eye after the side-by-side image comparison (Verification Image ticket).
    // No expense was committed yet: Skip discards the upload, Proceed logs it.
    const skipUpload = async () => {
        if (busy || resolution || !candidate.document_id) return;
        setBusy('skip'); setError(null);
        try {
            await documentService.deleteDocument(candidate.document_id);
            window.dispatchEvent(new CustomEvent('omnis:document-deleted', { detail: { id: candidate.document_id } }));
            setResolution(t('msgSkippedDiscardedUpload', 'Skipped — discarded the duplicate upload.'));
        } catch (_) {
            setError(t('msgCouldNotDiscardUpload', 'Could not discard the upload. Please try again.'));
        } finally { setBusy(null); }
    };
    const proceedUpload = async () => {
        if (busy || resolution || !candidate.document_id) return;
        setBusy('proceed'); setError(null);
        try {
            await expenseService.createExpense({
                document_id: candidate.document_id,
                book_id: candidate.book_id || null,
                merchant: candidate.merchant,
                amount: candidate.amount,
                currency: candidate.currency,
                expense_date: candidate.expense_date,
                direction: candidate.direction || 'expense',
            });
            window.dispatchEvent(new CustomEvent('omnis:expense-created', { detail: { document_id: candidate.document_id } }));
            setResolution(t('msgLoggedAnywayIn', 'Logged it anyway in {book}.', { book: candidate.book_name || t('msgUnassignedFallback', 'Unassigned') }));
        } catch (_) {
            setError(t('msgCouldNotLogExpense', 'Could not log the expense. Please try again.'));
        } finally { setBusy(null); }
    };
    return (
        <div className="mt-2 border border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10 rounded-lg p-3">
            <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-2">
                {isTransfer
                    ? t('msgTicketMayExistIn', 'This ticket may already exist in {target}', { target: targetBook })
                    : t('msgPossibleDuplicateDetected', 'Possible duplicate detected')}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <DuplicateRow row={existing[0]} label={isTransfer ? t('msgAlreadyInTarget', 'Already in {target}', { target: targetBook }) : t('msgAlreadyInLedger', 'Already in your ledger')} accent="amber" onNavigate={onNavigate} onPreview={onPreview} onDownload={onDownload} />
                <DuplicateRow row={candidate} label={isTransfer ? t('msgBeingMovedFrom', 'Being moved from {source}', { source: sourceBook }) : t('msgNewUpload', 'New upload')} accent="rose" onNavigate={onNavigate} onPreview={onPreview} onDownload={onDownload} />
            </div>
            {existing.length > 1 && (
                <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-400">
                    {t('msgMoreMatches', '+{n} more match{es} {place}.', {
                        n: existing.length - 1,
                        es: existing.length - 1 === 1 ? '' : 'es',
                        place: isTransfer ? t('msgInTargetBook', 'in {target}', { target: targetBook }) : t('msgInYourLedger', 'in your ledger'),
                    })}
                </div>
            )}
            {isTransfer ? (
                <div className="mt-3">
                    {resolution ? (
                        <div className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-700 dark:text-emerald-300">
                            <Check size={14} /> {resolution}
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={deleteFromSource}
                                    disabled={!!busy}
                                    title={t('msgDeleteDuplicateFromTitle', 'Delete the duplicate from {source} — it already exists in {target}', { source: sourceBook, target: targetBook })}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-rose-700 dark:text-rose-300 bg-white/[0.04] border border-rose-300 dark:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded disabled:opacity-50"
                                >
                                    {busy === 'delete' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                    {t('msgDeleteDuplicateFromBtn', 'Delete duplicate from {source}', { source: sourceBook })}
                                </button>
                                <button
                                    type="button"
                                    onClick={keepInSource}
                                    disabled={!!busy}
                                    title={t('msgKeepDuplicateInTitle', 'Keep the duplicate in {source} — cancels the move', { source: sourceBook })}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-pai-secondary bg-white/[0.04] border border-pai hover:bg-white/[0.07] rounded disabled:opacity-50"
                                >
                                    <X size={12} /> {t('msgKeepDuplicateInBtn', 'Keep duplicate in {source}', { source: sourceBook })}
                                </button>
                                <button
                                    type="button"
                                    onClick={moveAnyway}
                                    disabled={!!busy}
                                    title={t('msgMoveAnywayTitle', 'Move it into {target} anyway — creates a second copy', { target: targetBook })}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-blue-700 dark:text-blue-300 bg-white/[0.04] border border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded disabled:opacity-50"
                                >
                                    {busy === 'move' ? <Loader2 size={12} className="animate-spin" /> : <ChevronRight size={12} />}
                                    {t('msgMoveAnywayBtn', 'Move anyway')}
                                </button>
                            </div>
                            {error && <div className="mt-1.5 text-[11px] text-rose-600 dark:text-rose-400">{error}</div>}
                            <div className="mt-2 text-[11px] text-pai-meta">
                                {t('msgCustomInstructionPrefix', 'Or type a custom instruction below — e.g.')} <span className="italic">{t('msgCustomInstructionExample', '"move all duplicates to a new book called Check Later"')}</span>.
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="mt-3">
                    {resolution ? (
                        <div className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-700 dark:text-emerald-300">
                            <Check size={14} /> {resolution}
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    onClick={skipUpload}
                                    disabled={!!busy}
                                    title={t('msgDiscardDuplicateUploadTitle', 'Discard this duplicate upload')}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-rose-700 dark:text-rose-300 bg-white/[0.04] border border-rose-300 dark:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded disabled:opacity-50"
                                >
                                    {busy === 'skip' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                    {t('msgSkipDiscardUploadBtn', 'Skip (discard upload)')}
                                </button>
                                <button
                                    type="button"
                                    onClick={proceedUpload}
                                    disabled={!!busy}
                                    title={t('msgLogAnywaySeparateTitle', 'Log it anyway as a separate transaction')}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-blue-700 dark:text-blue-300 bg-white/[0.04] border border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded disabled:opacity-50"
                                >
                                    {busy === 'proceed' ? <Loader2 size={12} className="animate-spin" /> : <Wallet size={12} />}
                                    {t('msgProceedLogAnywayBtn', 'Proceed (log anyway)')}
                                </button>
                            </div>
                            {error && <div className="mt-1.5 text-[11px] text-rose-600 dark:text-rose-400">{error}</div>}
                            <div className="mt-2 text-[11px] text-pai-meta">
                                {t('msgCompareReceiptsPrefix', 'Compare the receipts above, then choose — or reply')} <span className="font-semibold">{t('msgProceedWord', '"proceed"')}</span> / <span className="font-semibold">{t('msgSkipWord', '"skip"')}</span>.
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// Failed-OCR notification (ticket #57). When create_expense* can't read a
// receipt's amount, NO ledger row is created (no "ghost" entry) and this card
// tells the user plainly, showing the IMAGE PREVIEW of each failed upload so they
// can identify exactly which document had the problem. Module-level so the
// ReceiptThumb blob image stays mounted (no flicker — same lesson as Disco Night).
const OcrFailedCard = ({ payload }) => {
    const { t } = useLanguage();
    const failed = Array.isArray(payload?.failed) ? payload.failed.filter((f) => f?.document_id) : [];
    if (!failed.length) return null;
    return (
        <div data-testid="ocr-failed-card" className="mt-2 border border-rose-300 dark:border-rose-700 bg-rose-50/50 dark:bg-rose-900/15 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-800 dark:text-rose-300 mb-1">
                <AlertTriangle size={14} />
                {t('msgOcrUnableToExtract', 'We were unable to extract data from {target}', { target: failed.length === 1 ? t('msgOcrThisImage', 'this image') : t('msgOcrNImages', '{n} images', { n: failed.length }) })}
            </div>
            <div className="text-[11px] text-pai-meta mb-2">
                {/* #524: the next step (retry first, then manual entry / a new picture) is PAI's reply, not the card. */}
                {t('msgOcrNoTransactionLogged', 'No transaction was logged.')}
            </div>
            <div className="space-y-2">
                {failed.map((f, i) => (
                    <figure key={f.document_id || i} className="m-0">
                        <ReceiptThumb documentId={f.document_id} label={t('msgOcrUnreadableLabel', '{name} (unreadable)', { name: f.merchant || t('msgReceiptFallback', 'Receipt') })} />
                        <figcaption className="mt-0.5 text-[10px] text-pai-meta truncate">
                            {f.merchant ? t('msgOcrCouldntReadTotal', "{name} — couldn't read total", { name: f.merchant }) : t('msgOcrCouldntReadReceipt', "Couldn't read this receipt")}
                        </figcaption>
                    </figure>
                ))}
            </div>
        </div>
    );
};

// create_expense_batch's intra-batch dedup result (Finance batch duplicates
// ticket). The tool already committed one copy of each duplicate set; this card
// reports "[X] unique / [Y] duplicates" and offers two bulk one-click actions on
// the REDUNDANT copies: Eliminate Duplicates (delete the redundant files) or
// Add Regardless (save them as separate transactions). Only shown when the batch
// actually contained duplicates.
const BatchDuplicateCard = ({ payload, conversationId, messageId }) => {
    const { t } = useLanguage();
    const book = payload?.book_name || t('msgUnassignedFallback', 'Unassigned');
    const uniqueCount = payload?.unique_count ?? 0;
    const redundant = Array.isArray(payload?.redundant) ? payload.redundant : [];
    const dupCount = payload?.duplicate_count ?? redundant.length;
    // Seed from the PERSISTED resolution so the decision is one-and-done: once
    // the user has eliminated/added, the buttons never reappear after a reload
    // or conversation switch (Duplicate Management UI Reappears bug).
    const [resolution, setResolution] = useState(payload?.resolution || null);
    const [busy, setBusy] = useState(null); // 'eliminate' | 'add'
    const [error, setError] = useState(null);
    if (!dupCount || redundant.length === 0) return null;

    // Persist the decision to the assistant message's metadata so it survives
    // reload / conversation switch. Best-effort — the optimistic local state
    // already cleared the buttons; a failed save just means they could reappear.
    const persist = async (text, action) => {
        if (!conversationId || !messageId) return;
        try { await apiService.resolveBatchDuplicate(conversationId, messageId, text, action); }
        catch (_) { /* non-fatal — local state already reflects the decision */ }
    };

    const eliminate = async () => {
        if (busy || resolution) return;
        setBusy('eliminate'); setError(null);
        try {
            for (const r of redundant) {
                if (!r.document_id) continue;
                // A redundant file already removed by a prior attempt is fine —
                // treat "not found" as success so a re-run is never a hard error.
                try { await documentService.deleteDocument(r.document_id); }
                catch (err) { if (!/404|not found/i.test(err?.message || '')) throw err; }
            }
            const text = t('msgEliminatedRedundantFiles', 'Eliminated {n} redundant file{s} — your ledger keeps one copy of each.', { n: redundant.length, s: redundant.length === 1 ? '' : 's' });
            setResolution(text);
            await persist(text, 'eliminate');
        } catch (_) {
            setError(t('msgCouldNotDeleteRedundantFiles', 'Could not delete the redundant files. Please try again.'));
        } finally { setBusy(null); }
    };
    const addRegardless = async () => {
        if (busy || resolution) return;
        setBusy('add'); setError(null);
        try {
            for (const r of redundant) {
                await expenseService.createExpense({
                    book_id: payload?.book_id || null,
                    merchant: r.merchant,
                    amount: r.amount,
                    currency: r.currency,
                    expense_date: r.expense_date,
                    direction: r.direction || 'expense',
                    document_id: r.document_id || null,
                });
                window.dispatchEvent(new CustomEvent('omnis:expense-created', { detail: { document_id: r.document_id } }));
            }
            const text = t('msgAddedDuplicatesAsTransactions', 'Added {n} duplicate{s} as separate transaction{s2} in {book}.', { n: redundant.length, s: redundant.length === 1 ? '' : 's', s2: redundant.length === 1 ? '' : 's', book });
            setResolution(text);
            await persist(text, 'add');
        } catch (_) {
            setError(t('msgCouldNotAddDuplicates', 'Could not add the duplicates. Please try again.'));
        } finally { setBusy(null); }
    };

    const btn = 'inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded disabled:opacity-50 bg-white/[0.04] border';
    return (
        <div className="mt-2 border border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10 rounded-lg p-3">
            <div className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
                {t('msgBatchProcessedSummary', 'Batch processed — {unique} unique ticket{us} saved, {dup} duplicate{ds} found', {
                    unique: uniqueCount, us: uniqueCount === 1 ? '' : 's', dup: dupCount, ds: dupCount === 1 ? '' : 's',
                })}
            </div>
            <div className="text-[11px] text-pai-meta mb-2">
                {t('msgBatchOneVersionRecorded', 'One version of each duplicate is already recorded in')} <span className="font-medium">{book}</span>. {t('msgBatchChooseRedundant', 'Choose what to do with the redundant {copies}:', { copies: dupCount === 1 ? t('msgCopySingular', 'copy') : t('msgCopyPlural', 'copies') })}
            </div>
            {resolution ? (
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-emerald-700 dark:text-emerald-300">
                    <Check size={14} /> {resolution}
                </div>
            ) : (
                <>
                    {/* Visual verification: kept vs duplicate, side-by-side, directly
                        above the action buttons (Image preview ticket). */}
                    {redundant.some((r) => r.document_id) && (
                        <div data-testid="batch-duplicate-preview" className="mb-2 space-y-2">
                            {redundant.filter((r) => r.document_id).map((r, i) => (
                                <DuplicatePreviewPair
                                    key={r.document_id || i}
                                    original={r.original_document_id}
                                    duplicate={r.document_id}
                                    merchant={r.merchant}
                                    amount={r.amount}
                                    currency={r.currency}
                                />
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={eliminate}
                            disabled={!!busy}
                            title={t('msgEliminateDuplicatesTitle', 'Permanently delete the redundant duplicate files to save storage')}
                            className={`${btn} text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/30`}
                        >
                            {busy === 'eliminate' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                            {t('msgEliminateDuplicatesBtn', 'Eliminate Duplicates')}
                        </button>
                        <button
                            type="button"
                            onClick={addRegardless}
                            disabled={!!busy}
                            title={t('msgAddRegardlessTitle', 'Save the redundant copies as additional separate transactions')}
                            className={`${btn} text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30`}
                        >
                            {busy === 'add' ? <Loader2 size={12} className="animate-spin" /> : <Wallet size={12} />}
                            {t('msgAddRegardlessBtn', 'Add Regardless')}
                        </button>
                    </div>
                    {error && <div className="mt-1.5 text-[11px] text-rose-600 dark:text-rose-400">{error}</div>}
                </>
            )}
        </div>
    );
};


const ChatExpenseCardList = ({ payload, onNavigate }) => {
    const { t } = useLanguage();
    const expenses = payload?.expenses || [];
    if (expenses.length === 0) {
        return (
            <div className="mt-2 text-xs text-pai-meta italic">
                {t('msgNoTransactionsMatched', 'No transactions matched.')}
            </div>
        );
    }
    const formatAmount = (amount, currency = 'USD') => {
        if (amount == null) return '—';
        try {
            return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
        } catch (_) {
            return `${currency} ${Number(amount).toFixed(2)}`;
        }
    };
    return (
        <div className="mt-2 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-pai-meta flex items-center gap-1">
                <Wallet size={11} />
                {t('msgTransactionsCount', '{n} transactions', { n: payload.total_count })}
                {payload.filters?.book_name && ` · ${payload.filters.book_name}`}
            </div>
            {expenses.map((e) => {
                const isIncome = e.direction === 'income';
                const href = `/expenses?focus=${e.id}&book=${e.book_id || 'unassigned'}&overlay=1`;
                return (
                    <button
                        key={e.id}
                        onClick={() => onNavigate(href)}
                        className={`w-full text-left flex items-center gap-2 rounded-lg border bg-white/[0.04] px-2.5 py-1.5 hover:shadow-modal-sm transition-all ${
                            isIncome
                                ? 'border-l-4 border-l-emerald-500 border-y-gray-200 border-r-gray-200 dark:border-y-gray-700 dark:border-r-gray-700'
                                : 'border-l-4 border-l-rose-400 border-y-gray-200 border-r-gray-200 dark:border-y-gray-700 dark:border-r-gray-700'
                        }`}
                    >
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                                <span className={`text-sm font-bold ${isIncome ? 'text-emerald-700 dark:text-emerald-300' : 'text-white'}`}>
                                    {isIncome ? '+' : ''}
                                    {formatAmount(e.amount, e.currency)}
                                </span>
                                <span className="text-xs text-pai-secondary truncate">
                                    {e.merchant || t('msgUnknownFallback', 'Unknown')}
                                </span>
                            </div>
                            <div className="text-[10px] text-pai-meta">
                                {e.expense_date || t('msgNoDateFallback', 'no date')}
                                {e.book_name && ` · ${e.book_name}`}
                                {e.category && ` · ${e.category}`}
                            </div>
                        </div>
                        <ChevronRight size={14} className="text-pai-faint flex-shrink-0" />
                    </button>
                );
            })}
        </div>
    );
};

// Highlights every occurrence of `query` in `text` (accent- and case-insensitive).
//
// Find-in-conversation Bug #352. This used to mint its own GLOBAL match index —
// `matchStartIndex + localMatchCount`, where `matchStartIndex` came from counting
// query hits in the RAW markdown source before this text segment
// (`getMatchOffset`, now deleted). That arithmetic was the navigation bug: the
// rendered text is not the source text (markdown syntax, stripped [FUNCT] blocks,
// `message.display`), and the offset was resolved with `rawContent.indexOf(segment)`
// — the FIRST occurrence — so a repeated segment produced DUPLICATE ids and the
// counter's Nth match often had no element to scroll to. `if (el)` then swallowed
// it and the page sat still, exactly as reported.
//
// Numbering is now assigned AFTER render, by walking the DOM in document order
// (`useInChatSearch`), so a mark's index is its true position on screen and the
// counter is the number of marks the user can actually see. This component just
// flags them; it deliberately does NOT know the active index, so clicking an arrow
// no longer re-renders (and re-parses the markdown of) every message in the thread.
const HighlightedText = ({ text, query }) => {
    const parts = splitByMatches(text, query);
    if (!parts) return <>{text}</>;

    return (
        <>
            {parts.map((part, i) => {
                // One capture group in the split pattern => odd indices are the
                // matches. Positional, so it cannot disagree with the regex the
                // way a lowercase string comparison could for folded accents.
                if (i % 2 === 1) {
                    return (
                        <mark
                            key={i}
                            data-search-match=""
                            className="search-match rounded-sm px-0.5"
                        >
                            {part}
                        </mark>
                    );
                }
                return part ? <span key={i}>{part}</span> : null;
            })}
        </>
    );
};

// Redesigned voice bubble (UI Audio #87): a single minimalist card \u2014 a clean header
// (mic icon + "Voice message"/"Voice messages" + total duration), the audio
// player(s) stacked below, and ONE "Show transcription" toggle \u2014 replacing the old
// stacked green per-clip boxes ("MVP style"). No counts, no "segments", no artifacts.
//
// Outside Box media player (#140): the metadata (mic icon, "Voice message" label,
// "Show transcription" toggle) is NO LONGER enclosed in its own bordered/filled card.
// The wrapper is now a plain, borderless, transparent container so the metadata sits
// cleanly inside the chat bubble \u2014 only the media PLAYERS (VoiceClipPlayer) keep their
// own pill styling. The message timestamp lives outside this component, untouched.
const VoiceBubble = ({ message, renderedContent }) => {
    const { t } = useLanguage();
    // New shape: voiceSegments \u2014 array of segments, each with its own audio url +
    // transcription. Falls back to the legacy single `voice` shape so historical
    // messages keep rendering.
    const segments = Array.isArray(message.voiceSegments) && message.voiceSegments.length > 0
        ? message.voiceSegments
        : (message.voice ? [{ ...message.voice, transcription: message.content }] : []);

    const [showTranscription, setShowTranscription] = useState(false);

    if (segments.length === 0) return null;

    const isMulti = segments.length > 1;
    const fallback = typeof renderedContent === 'string' ? renderedContent : message.content;
    const noTranscription = t('msgNoTranscription', '[no transcription]');
    const segTranscript = (s, i) => s.transcription || (i === 0 ? fallback : '') || noTranscription;
    // Single-clip fallback transcript (kept whole so a legacy message without a
    // per-segment transcription still reads cleanly).
    const singleTranscript = segments
        .map((s, i) => s.transcription || (i === 0 ? fallback : ''))
        .filter(Boolean)
        .join('\n\n') || fallback || noTranscription;

    return (
        <div data-testid="voice-metadata-wrapper" className="w-full min-w-0 max-w-sm">
            {/* Header: white mic icon + label only \u2014 no duration timestamp (Final Design #87). */}
            <div className="flex items-center gap-1.5 mb-2 text-pai-secondary">
                <Mic size={14} className="text-white dark:text-white" />
                <span data-testid="voice-header" className="text-xs font-semibold">{isMulti ? t('msgVoiceMessagesPlural', 'Voice messages') : t('msgVoiceMessageSingular', 'Voice message')}</span>
            </div>
            <div className="space-y-1.5">
                {segments.map((seg, idx) => (
                    <VoiceClipPlayer key={seg.gcs_path || seg.audio_url || idx} segment={seg} />
                ))}
            </div>
            <button
                onClick={() => setShowTranscription((v) => !v)}
                className="mt-2 text-xs text-blue-500 dark:text-blue-400 hover:underline flex items-center gap-1"
            >
                <span>{showTranscription ? t('msgHideTranscription', 'Hide transcription') : t('msgShowTranscription', 'Show transcription')}</span>
                <span className="text-[10px]">{showTranscription ? '\u25B2' : '\u25BC'}</span>
            </button>
            {showTranscription && (
                <div className="mt-2 border-t border-pai-subtle pt-2">
                    {isMulti ? (
                        // Each clip's transcript under its own "Message N" header so the
                        // user knows which text belongs to which audio (Final Design #87).
                        <div className="space-y-2">
                            {segments.map((seg, idx) => (
                                <div key={seg.gcs_path || seg.audio_url || idx} data-testid="voice-transcript-segment">
                                    <div data-testid="voice-transcript-label" className="text-[11px] font-semibold text-pai-meta">
                                        {t('msgVoiceMessageN', 'Message {n}', { n: idx + 1 })}
                                    </div>
                                    <div className="text-sm text-pai-secondary whitespace-pre-wrap">
                                        {segTranscript(seg, idx)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div data-testid="voice-transcript" className="text-sm text-pai-secondary whitespace-pre-wrap">
                            {singleTranscript}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Verbatim "Download as document" launcher for assistant messages — calls
// /api/documents/render-from-message/<id>. The BE renders the stored
// markdown through the same engine the chat bubble uses (Chromium + theme
// CSS for PDFs) and streams the bytes back as an attachment — nothing is
// persisted to the user's file manager, this is a pure one-off download.
// (The chat-driven "save this as a PDF" command still saves to the file
// manager via the save_last_reply_as_document tool — different intent.)
const DownloadReplyAsDocumentMenu = ({ message }) => {
    const { t } = useLanguage();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [status, setStatus] = useState(null); // {ok, name} | {error}
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    const slug = useMemo(() => {
        const body = message?.content || '';
        const firstHeading = body.match(/^#+\s+(.+)$/m)?.[1]?.trim();
        const firstLine = body.split('\n').find((l) => l.trim())?.trim();
        const seed = (firstHeading || firstLine || 'Reply').slice(0, 80);
        const cleaned = seed.replace(/[\\\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
        return cleaned || 'Reply';
    }, [message?.content]);

    const download = async (ext) => {
        if (!message?.dbId) return;
        setBusy(true);
        setStatus(null);
        try {
            const filename = `${slug}.${ext}`;
            await documentService.downloadMessageAsDocument(message.dbId, filename);
            setStatus({ ok: true, name: filename });
            // Auto-close after a beat
            setTimeout(() => { setOpen(false); setStatus(null); }, 1500);
        } catch (err) {
            setStatus({ error: userErrorMessage(err, t) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div ref={wrapperRef} className="relative inline-block min-w-0 max-w-full">
            <button
                onClick={() => setOpen((v) => !v)}
                className="pai-tap-row text-xs text-emerald-700 dark:text-emerald-400 hover:text-emerald-900 dark:hover:text-emerald-300 hover:underline inline-flex items-center gap-1 min-w-0 max-w-full text-left"
                title={t('msgDownloadReplyTitle', 'Download this reply as a file (does not save to your file manager)')}
            >
                <FileDown size={12} className="shrink-0" />
                <span className="min-w-0 break-words">{t('msgDownloadAsDocument', 'Download as document')}</span>
            </button>
            {open && (
                /* #243: max-w-full keeps the 12rem menu inside the bubble at minimum width. */
                <div className="absolute left-0 mt-1 w-48 max-w-full bg-white/[0.04] border border-pai-subtle rounded-lg shadow-modal-sm z-30 py-1">
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-pai-faint">
                        {t('msgDownloadAsHeader', 'Download as')}
                    </div>
                    <button
                        onClick={() => download('pdf')}
                        disabled={busy}
                        className="w-full text-left px-3 py-1.5 text-xs text-pai-secondary hover:bg-white/[0.07] disabled:opacity-50"
                    >
                        {t('msgDownloadFormatPdf', 'PDF (.pdf)')}
                    </button>
                    <button
                        onClick={() => download('docx')}
                        disabled={busy}
                        className="w-full text-left px-3 py-1.5 text-xs text-pai-secondary hover:bg-white/[0.07] disabled:opacity-50"
                    >
                        {t('msgDownloadFormatWord', 'Word (.docx)')}
                    </button>
                    <button
                        onClick={() => download('txt')}
                        disabled={busy}
                        className="w-full text-left px-3 py-1.5 text-xs text-pai-secondary hover:bg-white/[0.07] disabled:opacity-50"
                    >
                        {t('msgDownloadFormatText', 'Plain text (.txt)')}
                    </button>
                    {(busy || status) && (
                        <div className="px-3 py-1.5 text-[11px] border-t border-pai-hairline">
                            {busy && <span className="text-pai-meta">{t('msgDownloadPreparing', 'Preparing…')}</span>}
                            {!busy && status?.ok && (
                                <span className="text-emerald-600 dark:text-emerald-400">{t('msgDownloadedName', 'Downloaded “{name}”', { name: status.name })}</span>
                            )}
                            {!busy && status?.error && (
                                <span className="text-rose-600 dark:text-rose-400">{status.error}</span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Remove a voice clip's transcription from the edit-mode text (first occurrence),
// then tidy the separators it leaves behind. Used when an audio component is
// deleted so its transcription doesn't linger as "ghost" text. If the user has
// hand-edited the transcription (no longer an exact substring) it's treated as
// manual text and kept. (Audio Edit Persistence bug.)
const stripTranscriptionText = (text, transcription) => {
    if (!text || !transcription) return text;
    const idx = text.indexOf(transcription);
    if (idx === -1) return text;
    const next = text.slice(0, idx) + text.slice(idx + transcription.length);
    return next.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
};

// The CURRENT instruction text for a segment (Transcription Overwrite #228): the
// user's correction (`edited_transcription`) when present, else the raw system
// `transcription`. The edit buffer / recompose logic works on the current
// instruction (what the user is editing), while `transcription` stays the faithful
// audio record shown under "Show transcription".
const currentTranscript = (s) => (s && (s.edited_transcription || s.transcription)) || '';

// Build a message body with the SAME [Typed]/[Voice N] structure the send flow
// uses (MainOfficeView.buildSendSnapshot), so an edited hybrid keeps its labeled
// blocks instead of dumping new audio transcriptions in as plain text (Ignore
// Voices bug #61). Labels are only added when there are multiple sources — a
// typed-only or single-clip message stays unlabeled, matching send.
const composeHybridContent = (typed, transcriptions) => {
    const t = (typed || '').trim();
    const voices = (transcriptions || []).map((s) => (s || '').trim()).filter(Boolean);
    if (t && voices.length === 0) return t;
    if (!t && voices.length === 1) return voices[0];
    if (!t && voices.length === 0) return '';
    const parts = [];
    if (t) parts.push(`[Typed]: ${t}`);
    voices.forEach((v, i) => parts.push(`[Voice ${i + 1}]: ${v}`));
    return parts.join('\n\n');
};

// Recover the user's typed text out of the edit buffer so we can recompose with
// fresh [Voice N] labels after a clip is added/removed. Handles both the labeled
// form this code produces and the unlabeled legacy/seed form (where the typed
// part is whatever's left once each clip's transcription is subtracted).
const extractTypedFromEdit = (content, segments) => {
    let t = content || '';
    if (!t.trim()) return '';
    if (/\[(?:Typed|Voice\s*\d+)\]:/i.test(t)) {
        return t.split('\n\n')
            .map((b) => b.trim())
            .filter((b) => b && !/^\[Voice\s*\d+\]:/i.test(b))
            .map((b) => b.replace(/^\[Typed\]:\s*/i, ''))
            .join('\n\n')
            .trim();
    }
    for (const s of (segments || [])) {
        const tr = currentTranscript(s).trim();
        if (tr) t = stripTranscriptionText(t, tr);
    }
    return t.trim();
};

// From the edited content + the segments, return the NEW transcription implied for
// each segment (or null when it can't be confidently attributed). Used at save time
// to detect which voice transcriptions the user corrected (Final Edit #130):
//   - Labeled content ([Typed]/[Voice N]) → each "[Voice N]:" block is that
// segment's new transcription (the same structure the send/edit flow produces).
//   - Unlabeled single-segment message → the whole content IS the transcription.
//   - Unlabeled multi-segment (rare/legacy) → unattributable, all null.
const parseEditedTranscriptions = (content, segments) => {
    const n = (segments || []).length;
    const out = new Array(n).fill(null);
    if (n === 0) return out;
    const text = content || '';
    if (/\[(?:Typed|Voice\s*\d+)\]:/i.test(text)) {
        for (const block of text.split(/\n\n+/)) {
            const m = block.trim().match(/^\[Voice\s*(\d+)\]:\s*([\s\S]*)$/i);
            if (m) {
                const idx = parseInt(m[1], 10) - 1;
                if (idx >= 0 && idx < n) out[idx] = m[2].trim();
            }
        }
    } else if (n === 1) {
        out[0] = text.trim();
    }
    return out;
};

// Elevated "Voice Message N Edited:" block (Final Edit #130) — when a voice
// transcription has been corrected, its final text shows at the TOP of the message
// turn (above the audio players), styled like a standard typed-input block and
// sequentially labeled, so the user sees their correction without opening the
// "Show transcription" toggle. It reads the correction from `edited_transcription`
// (Transcription Overwrite #228 — the raw `transcription` stays the untouched audio
// record); LEGACY messages edited before that fix carry the correction in
// `transcription` itself, so we fall back to it. The `edited` flag + correction ride
// in the message's voice_segments metadata (whitelisted by the edit endpoint), so
// these blocks survive a reload.
const EditedVoiceBlocks = ({ segments }) => {
    const { t } = useLanguage();
    if (!Array.isArray(segments)) return null;
    const editedText = (s) => (s.edited_transcription || s.transcription || '');
    const edited = segments
        .map((s, i) => ({ s, i }))
        .filter(({ s }) => s && s.edited && editedText(s).trim());
    if (edited.length === 0) return null;
    return (
        <div className="mb-2 space-y-2" data-testid="edited-voice-blocks">
            {edited.map(({ s, i }) => (
                <div key={s.gcs_path || s.audio_url || i} data-testid="edited-voice-block">
                    <div data-testid="edited-voice-label" className="text-[11px] font-semibold text-pai-meta mb-0.5">
                        {t('msgVoiceMessageEditedLabel', 'Voice Message {n} Edited:', { n: i + 1 })}
                    </div>
                    <div className="whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-white rounded-lg bg-white/[0.06] px-3 py-2">
                        {editedText(s)}
                    </div>
                </div>
            ))}
        </div>
    );
};

const Message = ({ message, isUser, isHighlighted, conversationId, isLastUserMessage, onEditMessage, onJumpToMessage, onReply, isReadOnly = false, searchQuery = '', isSelectedForReply = false, userInitials = 'U', onRetry = null, retryDisabled = false }) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    // Personalized chat avatars (Toggles ticket): the user bubble shows their
    // system initials (passed down from the authed user, same logic as the
    // IconRail corner avatar); the assistant bubble shows "PAI" instead of "AI".
    // F-5.1: `isSelectedForReply` is now a PRIMITIVE boolean computed by
    // ChatMessages (was derived here from a `selectedReplyIds` Set that defeated
    // the memo). It is compared in arePropsEqual so the highlight still updates.
    const [showContextModal, setShowContextModal] = useState(false);
    // #349: the Energy Receipt is the PRIMARY view now; ContextViewerModal is
    // the "technical details" one click deeper inside it.
    const [showEnergyReceipt, setShowEnergyReceipt] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [documentMenuPosition, setDocumentMenuPosition] = useState({ top: 0, left: 0 });
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewDocument, setPreviewDocument] = useState(null);
    const [versionHistoryDoc, setVersionHistoryDoc] = useState(null);

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState('');
    // Audio components being edited (Edit Bug ticket). Persisted segment shape:
    // { audio_url, gcs_path, duration, mime_type, transcription }.
    const [editSegments, setEditSegments] = useState([]);
    const editTextareaRef = useRef(null);
    const voiceRecorder = useVoiceRecorder(conversationId);   // #377 attribution

    // Does this message carry any audio (new multi-segment OR legacy single)?
    const messageHasVoice = !!message.voice
        || (Array.isArray(message.voiceSegments) && message.voiceSegments.length > 0);

    // Lazy-loaded full_prompt state
    const [fullPrompt, setFullPrompt] = useState(null);
    const [promptBreakdown, setPromptBreakdown] = useState(null);
    const [loadingContext, setLoadingContext] = useState(false);
    const [contextError, setContextError] = useState(null);
    // #460: the BE says the prompt snapshot was removed by the 90-day sweep.
    const [contextRetentionExpired, setContextRetentionExpired] = useState(false);

    // Created document management modal
    const [createdDocModal, setCreatedDocModal] = useState(null); // { id, name, folderId } | null
    // Session-local echo of a Manage-dialog save, so the card updates the instant
    // the user presses Save. It is NOT the durable fix — this state dies with the
    // component (conversation switch / reload). Persistence comes from the BE:
    // GET /conversations/<id> resolves every document-card event to the LIVE
    // document row before serializing (#330), so a reload is correct by
    // construction and this map only covers the moments before the next fetch.
    const [createdDocStatus, setCreatedDocStatus] = useState({}); // { [docId]: {...} }
    const autoShowRef = useRef(false);

    // Focus textarea when entering edit mode. Keyed on `isEditing` only — adding
    // `editContent.length` would move the caret to the end on every keystroke.
    useEffect(() => {
        if (isEditing && editTextareaRef.current) {
            editTextareaRef.current.focus();
            editTextareaRef.current.setSelectionRange(editContent.length, editContent.length);
        }
    }, [isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-show created document modal once when creation completes during streaming.
    // Only trigger for NEW messages (no dbId yet) — not when loading from conversation history.
    useEffect(() => {
        if (!autoShowRef.current && !message.dbId && (message.metadata?.pipeline === 'document_create' || message.metadata?.document_create_complete)) {
            const cc = message.metadata?.document_create_complete;
            if (cc?.success && cc?.document_id) {
                autoShowRef.current = true;
                setCreatedDocModal({ id: cc.document_id, name: cc.document_name, folderId: cc.folder_id ?? null });
            }
        }
        // One-shot (guarded by autoShowRef); keyed on the completion signal, not every
        // metadata field, so it fires exactly once when creation completes mid-stream.
    }, [message.metadata?.document_create_complete, message.dbId]); // eslint-disable-line react-hooks/exhaustive-deps

    // The Manage dialog reports ONE outcome per interaction (#330): a save that
    // may have renamed and/or moved, or a discard.
    const handleDocumentUpdated = useCallback((result) => {
        const docId = result?.documentId || createdDocModal?.id;
        if (docId && result?.action) {
            setCreatedDocStatus(prev => ({
                ...prev,
                [docId]: {
                    action: result.action,
                    newName: result.name,
                    newFolderId: result.folderId,
                    renamed: !!result.renamed,
                    moved: !!result.moved,
                },
            }));
        }
        setCreatedDocModal(null);
    }, [createdDocModal]);

    const handleStartEdit = () => {
        setEditContent(message.content);
        // Seed editable segments from the message's current audio. Normalise the
        // legacy single-voice shape into a segment (its transcription is the
        // message content) so both shapes manage uniformly.
        const segs = (Array.isArray(message.voiceSegments) && message.voiceSegments.length > 0)
            ? message.voiceSegments
            : (message.voice ? [{ ...message.voice, transcription: message.content }] : []);
        setEditSegments(segs.map((s) => ({ ...s })));
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        if (voiceRecorder.isRecording) voiceRecorder.cancel();
        setIsEditing(false);
        setEditContent('');
        setEditSegments([]);
    };

    const removeEditSegment = (index) => {
        // A clip's transcription is tied to its audio — deleting the audio must also
        // strip that text from the input, or it lingers as a "ghost" transcription
        // (Audio Edit Persistence bug). Recompose from the typed part + the REMAINING
        // clips so the [Voice N] labels stay correct (re-numbered) and an audio-only
        // message reverts to empty. (Ignore Voices bug #61.)
        const segsBefore = editSegments;
        const nextSegs = segsBefore.filter((_, i) => i !== index);
        setEditSegments(nextSegs);
        setEditContent((text) => composeHybridContent(
            extractTypedFromEdit(text, segsBefore),
            nextSegs.map((s) => currentTranscript(s)),
        ));
    };

    // Toggle recording: start, or stop + transcribe + append the new segment.
    const handleToggleEditRecording = async () => {
        if (voiceRecorder.isRecording) {
            const seg = await voiceRecorder.stopAndTranscribe();
            if (seg) {
                const segsBefore = editSegments;
                const nextSegs = [...segsBefore, seg];
                setEditSegments(nextSegs);
                // Recompose with [Voice N] labels so the new clip is a DISTINCT,
                // tagged block — not plain text merged into the typed input (Ignore
                // Voices bug #61). Keeps the AI's regenerated context structured too.
                setEditContent((text) => composeHybridContent(
                    extractTypedFromEdit(text, segsBefore),
                    nextSegs.map((s) => currentTranscript(s)),
                ));
            }
        } else {
            await voiceRecorder.start();
        }
    };

    // Whether the audio set changed during this edit session.
    const segmentsChanged = useMemo(() => {
        const before = (Array.isArray(message.voiceSegments) && message.voiceSegments.length > 0)
            ? message.voiceSegments
            : (message.voice ? [message.voice] : []);
        if (before.length !== editSegments.length) return true;
        const key = (s) => `${s.gcs_path || s.audio_url || ''}`;
        return before.map(key).join('|') !== editSegments.map(key).join('|');
    }, [message.voiceSegments, message.voice, editSegments]);

    const editDirty = (editContent.trim() && editContent.trim() !== message.content) || segmentsChanged;

    const handleConfirmEdit = () => {
        const text = editContent.trim();
        // Backend requires non-empty content; block save if the user emptied it.
        if (!text || !editDirty || !onEditMessage) { setIsEditing(false); return; }
        // For voice/hybrid messages, send the audio patch alongside the text.
        // Empty segments → the message becomes text-only.
        let voicePatch = null;
        if (messageHasVoice || segmentsChanged) {
            // Detect which voice transcriptions the user CORRECTED (Final Edit #130 /
            // Transcription Overwrite #228): the new instruction per segment parsed from
            // the edited content. The correction is stamped into a SEPARATE
            // `edited_transcription` field and the raw `transcription` is PRESERVED
            // untouched — so "Show transcription" always shows the faithful audio record
            // and only the elevated "Voice Message N Edited:" block carries the
            // correction. Both ride in voice_segments (whitelisted wholesale), so they
            // survive a reload.
            const newTranscripts = parseEditedTranscriptions(text, editSegments);
            const hasLabels = /\[(?:Typed|Voice\s*\d+)\]:/i.test(text);
            const voiceSegmentsPatch = editSegments.map((s, i) => {
                const base = {
                    audio_url: s.audio_url,
                    gcs_path: s.gcs_path,
                    duration: s.duration,
                    mime_type: s.mime_type,
                    transcription: s.transcription || '', // ORIGINAL audio record — never overwritten
                };
                const original = (s.transcription || '').trim();
                const nt = newTranscripts[i];
                // The instruction differs from the original audio record → a correction.
                if (nt != null && nt !== original) {
                    return { ...base, edited_transcription: nt, edited: true };
                }
                // Attributable and equal to the original → the user reverted; clear the edit.
                if (nt != null) return base;
                // Unattributable (unlabeled multi-clip) → carry any prior correction forward.
                if (s.edited && (s.edited_transcription || '').trim()) {
                    return { ...base, edited_transcription: s.edited_transcription, edited: true };
                }
                return base;
            });
            // typed_text = ONLY the genuinely-typed part so it isn't duplicated by the
            // edited block: the [Typed] block for labeled content; empty when the whole
            // (unlabeled) content was attributed to a single voice transcription; the
            // legacy strip-transcriptions fallback otherwise (FE Ignored Text #60 /
            // Ignore Voices #61). The bubble renders `typedText` above the players.
            const attributedToVoice = !hasLabels && newTranscripts.some((t) => t != null);
            voicePatch = {
                voice_segments: voiceSegmentsPatch,
                voice: null, // drop the legacy single-voice shape on any audio edit
                typed_text: attributedToVoice ? '' : extractTypedFromEdit(text, editSegments),
            };
        }
        onEditMessage(message.id, message.dbId, text, voicePatch);
        setIsEditing(false);
        setEditSegments([]);
    };

    const handleEditKeyDown = (e) => {
        if (e.key === 'Escape') {
            handleCancelEdit();
        } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleConfirmEdit();
        }
    };

    // Check if message was edited (updated_at > created_at)
    const wasEdited = message.updated_at && message.created_at &&
        new Date(message.updated_at) > new Date(message.created_at);

    // Close document menu on Escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                handleCloseDocumentMenu();
            }
        };

        if (selectedDocument) {
            document.addEventListener('keydown', handleEscape);
            return () => document.removeEventListener('keydown', handleEscape);
        }
    }, [selectedDocument]);

    const handleDocumentClick = (event, documentId, documentName) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDocumentMenuPosition({
            top: rect.bottom + window.scrollY + 5,
            left: rect.left + window.scrollX
        });
        setSelectedDocument({ id: documentId, name: documentName });
    };

    const handleCloseDocumentMenu = () => {
        setSelectedDocument(null);
    };

    const handleDownloadDocument = async (documentId, documentName) => {
        try {
            const token = getAuthToken();
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/documents/${documentId}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Download failed');
            }

            // Create a blob from the response
            const blob = await response.blob();

            // Create a download link and trigger it
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = documentName;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            // Close menu after successful download
            handleCloseDocumentMenu();
        } catch (error) {
            console.error('Error downloading document:', error);
            alert(t('msgFailedToDownloadDocument', 'Failed to download document. Please try again.'));
        }
    };

    const handlePreviewDocument = useCallback((documentId, documentName) => {
        setPreviewDocument({ id: documentId, name: documentName });
        setShowPreviewModal(true);
        handleCloseDocumentMenu();
    }, []);

    // Fetch full_prompt on-demand when modal opens
    const handleOpenContextModal = useCallback(async () => {
        setShowContextModal(true);

        // If we already have prompt_breakdown from streaming metadata, use it directly
        if (message.metadata?.prompt_breakdown) {
            setPromptBreakdown(message.metadata.prompt_breakdown);
            if (message.metadata.full_prompt) {
                setFullPrompt(message.metadata.full_prompt);
            }
            return;
        }

        // If we already have the full_prompt (from streaming), use it
        if (message.metadata?.full_prompt) {
            setFullPrompt(message.metadata.full_prompt);
            return;
        }

        // If we already loaded it, don't fetch again
        if (fullPrompt || promptBreakdown) {
            return;
        }

        // Fetch from backend
        if (conversationId && message.dbId) {
            setLoadingContext(true);
            setContextError(null);
            try {
                const token = getAuthToken();
                const response = await fetch(
                    `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/conversations/${conversationId}/messages/${message.dbId}/context`,
                    {
                        headers: { 'Authorization': `Bearer ${token}` }
                    }
                );

                if (!response.ok) {
                    throw new Error(t('msgCtxFailedToLoad', 'Failed to load context'));
                }

                const data = await response.json();
                setContextRetentionExpired(!!data.retention_expired);
                setFullPrompt(data.full_prompt);
                if (data.prompt_breakdown) {
                    setPromptBreakdown(data.prompt_breakdown);
                }
            } catch (err) {
                console.error('Error loading context:', err);
                setContextError(userErrorMessage(err, t));
            } finally {
                setLoadingContext(false);
            }
        }
    }, [conversationId, message.dbId, message.metadata?.full_prompt, message.metadata?.prompt_breakdown, fullPrompt, promptBreakdown, t]);

    const handleOpenEnergyReceipt = useCallback(() => {
        // A turn that isn't persisted yet has no id for the backend to bound a
        // turn window with, so there is no receipt to show — fall through to the
        // raw trace (which streaming metadata alone can serve) rather than
        // opening an empty modal.
        if (conversationId && message.dbId) {
            setShowEnergyReceipt(true);
            return;
        }
        handleOpenContextModal();
    }, [conversationId, message.dbId, handleOpenContextModal]);

    // "Technical details", from inside the receipt: hand off to the raw trace
    // viewer, which still owns the lazy full_prompt fetch.
    const handleOpenTechnical = useCallback(() => {
        setShowEnergyReceipt(false);
        handleOpenContextModal();
    }, [handleOpenContextModal]);

    // Memoize document sources calculation to avoid re-computing on every render
    // Only show documents that are actually cited in the answer (not all retrieved docs)
    const { documentSources, uniqueDocNames } = useMemo(() => {
        if (!message.metadata?.sources?.length) {
            return { documentSources: [], uniqueDocNames: [] };
        }
        // Drop sources that point at a doc this message itself created/edited —
        // showing the just-produced PDF as a "context document" is misleading.
        // The agentic flow tracks the new doc in documents_used so the model can
        // reference it on later turns; for display we exclude it.
        // Extra Documents #187: exclude EVERY doc this turn created/edited (a turn can
        // produce many), not just the first.
        const ownProducedIds = new Set(
            [
                message.metadata.document_create_complete?.document_id,
                message.metadata.document_edit_complete?.document_id,
                ...(message.metadata.document_creates || []).map((c) => c?.document_id),
                ...(message.metadata.document_edits || []).map((e) => e?.document_id),
            ].filter(Boolean)
        );
        const sources = message.metadata.sources.filter(
            s => s.document_name && !ownProducedIds.has(s.document_id)
        );

        // Show ONLY the documents the answer actually references ("Verified Sources").
        // A document retrieved during search but never mentioned in the reply is noise
        // and must be hidden (RAG-transparency ticket). The model is instructed to cite
        // with **[Document Name]** notation, but in practice it also uses [Name], **Name**,
        // with-or-without the file extension, or a plain in-text mention — so match all of
        // those rather than the old bracket+extension-only regex (which missed most real
        // citations and then fell back to dumping EVERY retrieved doc).
        const content = (message.content || '');
        const contentLower = content.toLowerCase();
        const stripExt = (s) => s.replace(/\.[^.]+$/, '');

        // Citation texts the model emitted inside [...] or **...** wrappers.
        const citationTexts = new Set();
        for (const re of [/\[([^\]\n]{2,})\]/g, /\*\*([^*\n]{2,})\*\*/g]) {
            let m;
            while ((m = re.exec(content)) !== null) citationTexts.add(m[1].trim().toLowerCase());
        }

        const isReferenced = (docName) => {
            const full = (docName || '').trim().toLowerCase();
            if (!full) return false;
            const base = stripExt(full);
            // a) explicit [..]/**..** citation matching the name or its base (extension-agnostic)
            for (const c of citationTexts) {
                if (c === full || c === base || stripExt(c) === base) return true;
            }
            // b) the full filename (with extension) appears verbatim in the reply text
            if (full.includes('.') && contentLower.includes(full)) return true;
            return false;
        };

        // No "show all" fallback: if the reply references no document, the ledger is empty.
        const citedSources = sources.filter(s => isReferenced(s.document_name));
        const uniqueNames = [...new Set(citedSources.map(s => s.document_name))];
        return { documentSources: citedSources, uniqueDocNames: uniqueNames };
        // #187: the memo reads document_creates/document_edits (to exclude every doc
        // this turn produced), so they must be deps or the source list goes stale.
    }, [message.metadata?.sources, message.metadata?.document_create_complete?.document_id, message.metadata?.document_edit_complete?.document_id, message.metadata?.document_creates, message.metadata?.document_edits, message.content]);

    // Memoize markdown component config to prevent recreation on each render
    const markdownComponents = useMemo(() => {
        // Wrap every STRING child in the highlighter. Match numbering is no longer
        // computed here (see `HighlightedText`) — the DOM walk assigns it after
        // render — so this is a pure, order-independent transform.
        const highlightStringChildren = (children) => {
            if (!searchQuery) return children;
            return React.Children.map(children, (child) => {
                if (typeof child === 'string' && child.length > 0) {
                    return <HighlightedText text={child} query={searchQuery} />;
                }
                return child;
            });
        };

        return {
            // Backticks Bug #287 (re-open) — a fenced block is rendered HERE, not in
            // `code`. react-markdown v9+ REMOVED the `inline` prop, so the old
            // `!inline && match` test collapsed to `match`: a fence with NO language
            // tag has no `language-*` class and fell through to the INLINE pill
            // branch, painting a near-white `bg-white/[0.06]` pill inside the dark
            // `<pre>` while `.prose :where(pre code)` forced `color: inherit`
            // (= --tw-prose-pre-code #e5e7eb). Light mode: #e5e7eb on #f3f4f6 =
            // 1.13:1 — the PO's "background highlight with a complete lack of
            // contrast". A `<pre>` is unambiguously a block, so keying off it can
            // never regress the way an absent prop did.
            pre: ({ node, children }) => {
                const codeNode = (node?.children || []).find((c) => c.tagName === 'code');
                if (!codeNode) return <pre>{children}</pre>;
                const cls = [].concat(codeNode.properties?.className || []).join(' ');
                const match = /language-(\w+)/.exec(cls);
                const text = (codeNode.children || []).map((c) => c.value || '').join('');
                return <CodeBlock code={text.replace(/\n$/, '')} language={match ? match[1] : ''} />;
            },
            // Inline-only now (blocks are handled by `pre` above).
            code: ({ node, className, children, ...props }) => (
                <code className="bg-white/[0.06] px-1 py-0.5 rounded text-sm break-words [overflow-wrap:anywhere]" {...props}>
                    {highlightStringChildren(children)}
                </code>
            ),
            // #477: a mandatory professional disclaimer (its own paragraph,
            // starting with the literal `Disclaimer:` marker the BE prompt
            // requires) renders as a small, muted, italic block set apart from
            // the answer. ASSISTANT bubbles only — a user typing "Disclaimer:"
            // keeps a normal paragraph.
            p: ({ node, children, ...props }) => (
                !isUser && isDisclaimerText(hastText(node)) ? (
                    <p
                        {...props}
                        data-testid="assistant-disclaimer"
                        className="mt-4 mb-0 px-3 py-2 rounded-seg border border-pai-hairline bg-white/[0.02] text-xs leading-relaxed italic text-pai-meta"
                    >
                        {highlightStringChildren(children)}
                    </p>
                ) : (
                    <p {...props}>{highlightStringChildren(children)}</p>
                )
            ),
            li: ({ node, children, ...props }) => (
                <li {...props}>{highlightStringChildren(children)}</li>
            ),
            td: ({ node, children, ...props }) => (
                <td className="px-4 py-2 text-sm text-white" {...props}>
                    {highlightStringChildren(children)}
                </td>
            ),
            h1: ({ node, children, ...props }) => (
                <h1 {...props}>{highlightStringChildren(children)}</h1>
            ),
            h2: ({ node, children, ...props }) => (
                <h2 {...props}>{highlightStringChildren(children)}</h2>
            ),
            h3: ({ node, children, ...props }) => (
                <h3 {...props}>{highlightStringChildren(children)}</h3>
            ),
            // #352 "finds only a fraction": these four render visible prose but had
            // no highlight wrapper, so a hit inside a link, a quote, a deeper
            // heading or a table HEADER was silently invisible to the search.
            h4: ({ node, children, ...props }) => (
                <h4 {...props}>{highlightStringChildren(children)}</h4>
            ),
            h5: ({ node, children, ...props }) => (
                <h5 {...props}>{highlightStringChildren(children)}</h5>
            ),
            h6: ({ node, children, ...props }) => (
                <h6 {...props}>{highlightStringChildren(children)}</h6>
            ),
            a: ({ node, children, ...props }) => (
                <a {...props}>{highlightStringChildren(children)}</a>
            ),
            blockquote: ({ node, children, ...props }) => (
                <blockquote {...props}>{highlightStringChildren(children)}</blockquote>
            ),
            strong: ({ node, children, ...props }) => (
                <strong {...props}>{highlightStringChildren(children)}</strong>
            ),
            em: ({ node, children, ...props }) => (
                <em {...props}>{highlightStringChildren(children)}</em>
            ),
            del: ({ node, children, ...props }) => (
                <del {...props}>{highlightStringChildren(children)}</del>
            ),
            table: ({ node, ...props }) => (
                <div className="overflow-x-auto my-4">
                    <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 border border-pai" {...props} />
                </div>
            ),
            thead: ({ node, ...props }) => (
                <thead className="bg-white/[0.05]" {...props} />
            ),
            tbody: ({ node, ...props }) => (
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600 bg-white/[0.04]" {...props} />
            ),
            tr: ({ node, ...props }) => (
                <tr className="hover:bg-white/[0.07]" {...props} />
            ),
            th: ({ node, children, ...props }) => (
                <th className="px-4 py-2 text-left text-xs font-medium text-pai-secondary uppercase tracking-wider" {...props}>
                    {highlightStringChildren(children)}
                </th>
            )
        };
        // `activeMatchIndex` / `matchStartIndex` are deliberately GONE from the deps
        // (#352): the active mark is now styled imperatively by `useInChatSearch`,
        // so pressing an arrow used to re-parse the markdown of every message in the
        // thread for a change that is one CSS class on one element.
    }, [searchQuery, isUser]);

    // `normalizeTex` is passed for ASSISTANT turns only (Right Arrow #340): the
    // model sometimes emits inline LaTeX (`$\rightarrow$`, `$\times$`) and
    // nothing here renders it, so it reaches the bubble as a raw escape. It is
    // applied per TEXT part — a [FUNCT] code part is never rewritten — and
    // never to a user turn, since we must not rewrite what the user typed.
    const renderContent = useCallback((content, normalizeTex = false) => {
        let cleanedContent = content.replace(/[[]\]\/?(SUB|INST)[\]]/g, '');

        const codeBlockRegex = /[[]FUNCT[\]]([\s\S]*?)[[]\/FUNCT[\]]/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        while ((match = codeBlockRegex.exec(cleanedContent)) !== null) {
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: cleanedContent.slice(lastIndex, match.index)
                });
            }

            parts.push({
                type: 'code',
                language: 'python',
                content: match[1].trim()
            });

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < cleanedContent.length) {
            parts.push({
                type: 'text',
                content: cleanedContent.slice(lastIndex)
            });
        }

        if (parts.length === 0) {
            parts.push({ type: 'text', content: cleanedContent });
        }

        return parts.map((part, index) => {
            if (part.type === 'code') {
                return <CodeBlock key={index} code={part.content} language={part.language} />;
            }
            return (
                <div key={index} className="prose prose-sm max-w-none min-w-0 dark:prose-invert break-words [overflow-wrap:anywhere]">
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                    >
                        {normalizeTex ? separateDisclaimerLines(normalizeTexSymbols(part.content)) : part.content}
                    </ReactMarkdown>
                </div>
            );
        });
    }, [markdownComponents]);

    // #439: the turn's error state (set by MainOfficeView from the stream's
    // error chunk, or on a failed request) — persisted in metadata, so it
    // survives a reload.
    const streamError = message.metadata?.stream_error || null;

    // #457: a STOPPED NOTICE — the server saves it when the user stops a turn
    // (metadata.stopped = {run_id, stopped_by, at}); the Main Office also
    // renders one locally the instant Stop is pressed. Any text the run had
    // already produced rides in metadata.partial_text and is shown above the
    // status line. ChatMessages hides the notice once a newer reply exists.
    const stoppedInfo = !isUser && message.metadata?.stopped ? message.metadata.stopped : null;

    // Memoize the rendered content to avoid re-parsing markdown on every render
    const renderedContent = useMemo(() => {
        if (message.isSearching) {
            return null; // Will be rendered differently
        }
        const content = isUser
            ? (message.display || message.content)
            : (message.metadata?.stopped ? (message.metadata?.partial_text || '') : message.content);
        if (!content) return null;
        return renderContent(content, !isUser);
        // `searchQuery` is retained deliberately: it drives the highlighting inside
        // renderContent, which reads it via closure rather than directly here. The
        // active match index is NOT a dep any more (#352) — it is applied to one DOM
        // node imperatively, so moving it must not re-parse this message's markdown.
    }, [message.content, message.display, message.isSearching, message.metadata?.stopped, message.metadata?.partial_text, isUser, renderContent, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div
            id={`message-${message.id}`}
            data-testid={isUser ? 'message-row-user' : 'message-row-assistant'}
            /* The jump-to-original highlight as a STRUCTURAL fact (like the sidebar's
               data-current): its ring colour already changed once in the redesign. */
            data-highlighted={isHighlighted ? 'true' : 'false'}
            className={`group relative flex gap-2.5 px-4 py-2.5 transition-colors duration-300 ${
                /* New UI #98: chat-app-style directional layout — user turns align to the
                   RIGHT (avatar on the right via flex-row-reverse), PAI turns to the LEFT.
                   The bubble background now lives on the content wrapper, so the row itself
                   is transparent (Color Sync #88 still holds: both bubbles share one color). */
                isUser ? 'flex-row-reverse' : ''
            } ${isHighlighted ? 'ring-4 ring-pai-amber/60 rounded-2xl' : ''} ${isSelectedForReply ? 'ring-2 ring-pai-blue rounded-2xl' : ''}`}
        >
            {/* Standardized white circle / black initials (New Design #72) — a
                high-contrast minimalist avatar; the border keeps the white circle
                defined on a light chat background. */}
            <div data-testid="msg-avatar" className="shrink-0 self-start w-[38px] h-[38px] rounded-full flex items-center justify-center bg-white text-pai-void text-xs font-extrabold select-none">
                {isUser ? userInitials : 'PAI'}
            </div>
            {/* New UI #98: the message content is now a chat BUBBLE (rounded, padded,
                max 80% width) instead of a full-width row. One corner is squared off
                toward the avatar for the classic speech-bubble tail. */}
                <div data-testid="msg-bubble" className={`relative min-w-0 rounded-[15px] py-3 border border-white/[0.11] ${
                isUser ? 'max-w-[74%] bg-white/[0.05]' : 'max-w-[78%] bg-white/[0.035]'
                } ${
                /* Wiggle room (#219) + Move Buttons REPLY & EDIT (#224): the hover action
                   bar (msg-actions) is absolutely positioned in the bubble's top-right
                   corner and reserves no horizontal space, so on a USER bubble any text
                   reaching the top-right corner (a short content-width single line, or a
                   full-width first line) collides with the Reply arrow / Edit pencil.
                   Fix — USER bubbles only — reserve a right gutter (pr-14 ≈ 56px, enough
                   for both icons + their right offset) so the icons always have their own
                   space: it widens a content-width single-line bubble (~one word of
                   "wiggle room") AND keeps a full-width first line clear of the icons. The
                   absolutely-positioned action bar sits against the bubble's padding edge,
                   so the reserve pushes only the CONTENT — the icons stay in the corner and
                   assistant bubbles (px-4) are untouched.
                   #243 (re-open, Edit Mode): while EDITING, msg-actions is not rendered at
                   all (see the guard below), so the 56px gutter is dead space that steals a
                   quarter of the content box at the ~240px floor — exactly where the PO saw
                   Cancel pushed out. Reserve it only when the icons actually exist. */
                isUser ? `${isEditing ? 'px-[19px]' : 'pl-[19px] pr-14 touch:pr-[4.5rem]'} rounded-tr-md` : 'px-5 rounded-tl-md'
            }`}>
                {/* Message action bar (Reply Arrow #141) — Reply + Edit anchored INSIDE
                    the bubble's top-right corner. The Reply arrow always sits to the LEFT
                    of the Edit pencil and, being absolutely positioned within the bubble
                    (which is `relative`), it follows the message box as the chat expands /
                    contracts (full-width ↔ narrow half-view) and never hangs outside the
                    bubble or overlaps the avatar. Hover-only, except the Reply arrow stays
                    lit while the message is selected for reply. Hidden while editing /
                    streaming. */}
                {!message.isSearching && !isEditing && (
                    <div data-testid="msg-actions" className="absolute top-1 right-1 z-10 flex items-center gap-0.5 touch:gap-5">
                        {/* #432: on a finger the two icons sit 20px apart so each one's 40px
                            tap area is its own (the user-bubble gutter grows to match). */}
                        {!isReadOnly && onReply && (
                            <button
                                data-testid="msg-reply-btn"
                                onClick={() => onReply(message)}
                                className={`p-1 rounded transition-opacity ${
                                    isSelectedForReply
                                        ? 'opacity-100 text-blue-500 dark:text-blue-400'
                                        : 'opacity-0 group-hover:opacity-100 pai-touch-reveal text-white/45 hover:text-pai-blue'
                                }`}
                                title={isSelectedForReply ? t('msgRemoveFromReply', 'Remove from reply') : t('msgReplyToThisMessage', 'Reply to this message')}
                            >
                                <Reply size={15} />
                            </button>
                        )}
                        {isUser && isLastUserMessage && !isReadOnly && (
                            <button
                                data-testid="msg-edit-btn"
                                onClick={handleStartEdit}
                                className="p-1 rounded opacity-0 group-hover:opacity-100 pai-touch-reveal transition-opacity text-white/45 hover:text-pai-blue"
                                title={t('msgEditMessage', 'Edit message')}
                            >
                                <Pencil size={16} />
                            </button>
                        )}
                    </div>
                )}
                {/* Reply quote preview (supports single object or array) */}
                {message.repliedTo && (Array.isArray(message.repliedTo) ? message.repliedTo : [message.repliedTo]).filter(r => r?.content).map((reply, idx) => (
                    <div
                        key={reply.id || idx}
                        className={`mb-2 flex items-stretch rounded overflow-hidden border ${
                            reply.role === 'user'
                                ? 'border-green-200 dark:border-green-700/50'
                                : 'border-purple-200 dark:border-purple-700/50'
                        } ${reply.id ? 'cursor-pointer hover:opacity-75 transition-opacity' : ''}`}
                        onClick={() => {
                            // Jump to (and briefly highlight) the original message. The handler
                            // resolves the reply's DB id to whichever message is on screen — a
                            // live-session message renders under its client id, not its dbId.
                            if (reply.id && onJumpToMessage) onJumpToMessage(reply.id);
                        }}
                        title={reply.id ? t('msgClickToJumpOriginal', 'Click to jump to original message') : undefined}
                    >
                        <div className={`w-1 shrink-0 ${
                            reply.role === 'user' ? 'bg-green-500' : 'bg-purple-500'
                        }`} />
                        <div className="px-2 py-1.5 bg-white/[0.06] flex-1 min-w-0">
                            <div className={`text-xs font-semibold mb-0.5 ${
                                reply.role === 'user'
                                    ? 'text-green-700 dark:text-green-400'
                                    : 'text-purple-700 dark:text-purple-400'
                            }`}>
                                {reply.role === 'user' ? t('composerYou', 'You') : 'PAI'}
                            </div>
                            <div className="text-xs text-pai-meta line-clamp-2 break-words">
                                {reply.content}
                            </div>
                        </div>
                    </div>
                ))}
                {/* Collapsible thinking block — the collapsed summary header.
                    #240 Main Chat Timer: rendered whenever there are tool/reasoning
                    steps (now DURING processing too, not only after completion) so its
                    total-time figure in parens TICKS LIVE (now − procStartMs) until the
                    first response token freezes it to the wall-clock total. The dropdown
                    breakdown is unchanged. */}
                {message.toolSteps?.length > 0 && (
                    <ThinkingSteps
                        steps={message.toolSteps}
                        collapsed={true}
                        totalMs={message.processingMs ?? null}
                        startMs={message.procStartMs ?? null}
                        isProcessing={!!message.isSearching}
                    />
                )}
                <div className="text-white dark:text-white leading-relaxed">
                    {message.isSearching ? (
                        <ThinkingSteps steps={message.toolSteps || []} collapsed={false} />
                    ) : isUser && isEditing ? (
                        /* Inline editing mode. #243 (re-open): the whole editor is a
                           shrinkable column (min-w-0 / max-w-full) so nothing inside it can
                           establish a min-content floor wider than the bubble at the ~240px
                           narrow-split layout. */
                        <div data-testid="edit-mode-container" className="space-y-2 min-w-0 max-w-full">
                            <textarea
                                ref={editTextareaRef}
                                value={editContent}
                                onChange={(e) => setEditContent(e.target.value)}
                                onKeyDown={handleEditKeyDown}
                                /* Edit box update (#277): neutral, theme-standard surface —
                                   the mockup's blue tint/glow is deliberately NOT reproduced
                                   (PO: "ignore the weird blue shading"). The editor now reads
                                   as an inset field inside the bubble; only the FOCUS state is
                                   blue, like every other input on the platform. */
                                /* `block` kills the inline-block descender gap that left a
                                   ~5px sliver between the field and the panel — one of the
                                   "inconsistent margins" the ticket calls out. A 2-row floor
                                   (it still auto-grows with the content) keeps the field
                                   comfortable without parking two blank lines under a
                                   one-line message. */
                                className="block w-full p-3 border border-pai rounded-lg focus:border-white/80 bg-white/[0.05] text-white resize-none"
                                rows={Math.max(2, editContent.split('\n').length)}
                                placeholder={t('msgEditYourMessagePlaceholder', 'Edit your message...')}
                            />
                            {/* Audio component management (Edit Bug ticket + Edit Mode
                                record-button bug): manage existing clips OR record +
                                append new ones. ALWAYS rendered in edit mode so the
                                record trigger is available for EVERY message — including
                                a message that was originally text-only, which recording
                                turns into a hybrid (Text + Voice) update. The old gate
                                (only shown when the original message had audio) hid the
                                record button for text-only messages after a response OR
                                after Stop.

                                Edit box update (#277) restructured this into ONE cohesive
                                control panel, per the mockup: the clips, the action row
                                (Save & Regenerate / Add voice / Cancel) and the keyboard
                                hints all live inside a single bordered container directly
                                under the text area, rather than a green-tinted audio box
                                followed by a detached button row. That removes the seam and
                                the "empty chunk of space" the PO called out, and the green
                                accent gives way to the platform's neutral chrome. */}
                            {(
                                <div data-testid="edit-controls-panel" className="space-y-2 min-w-0 max-w-full rounded-lg border border-pai bg-white/[0.03] p-2">
                                    {editSegments.length === 0 && !voiceRecorder.isRecording && (
                                        <div className="text-xs text-pai-meta italic">
                                            {t('msgNoAudioTextOnly', 'No audio — this message will be saved as text-only.')}
                                        </div>
                                    )}
                                    {/* Sequential labeling for edited multi-audio messages (#121):
                                        each block stacks label (top) → corrected text (middle) →
                                        audio player (bottom). Single audio → "Voice Message Edited";
                                        multiple → "Voice Message N Edited". */}
                                    {editSegments.map((seg, idx) => {
                                        const segLabel = editSegments.length === 1
                                            ? t('msgVoiceMessageEditedSingular', 'Voice Message Edited')
                                            : t('msgVoiceMessageNEdited', 'Voice Message {n} Edited', { n: idx + 1 });
                                        return (
                                            <div key={seg.gcs_path || seg.audio_url || idx} data-testid="edit-voice-segment" className="space-y-1.5 min-w-0 max-w-full overflow-hidden text-xs bg-white/[0.04] rounded-md px-2 py-2 border border-pai-subtle">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <Mic size={13} className="text-pai-meta shrink-0" />
                                                    <span data-testid="edit-voice-label" className="font-medium text-pai-secondary flex-1 min-w-0 truncate">{segLabel}</span>
                                                    <button
                                                        onClick={() => removeEditSegment(idx)}
                                                        className="p-1 text-pai-faint hover:text-red-500 shrink-0"
                                                        title={t('msgRemoveAudio', 'Remove audio')}
                                                        aria-label={t('msgRemoveSegmentAria', 'Remove {label}', { label: segLabel })}
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                                {seg.transcription && (
                                                    <div className="text-pai-secondary whitespace-pre-wrap break-words [overflow-wrap:anywhere] px-0.5">{seg.transcription}</div>
                                                )}
                                                {(seg.audio_url || seg.gcs_path) && <VoiceClipPlayer segment={seg} />}
                                            </div>
                                        );
                                    })}
                                    {/* Action row (#277) — the mockup's single line:
                                        [Save & Regenerate] [Add voice] ……… [Cancel].
                                        "Add voice" sits with the primary actions instead of
                                        floating under the clips, so the panel ends on a
                                        purposeful row rather than the mockup's dead space.

                                        #243 (re-open, Edit Mode) is preserved verbatim: this
                                        row used to be a rigid `flex items-center gap-2` whose
                                        min-content far exceeded the bubble at the narrow-split
                                        floor, pushing Cancel clean out of the box. It WRAPS,
                                        and every control is min-w-0 so a squeezed button wraps
                                        its own label rather than overflowing. `ml-auto` on
                                        Cancel right-aligns it (mockup) and degrades safely to
                                        "first item on its own wrapped line". */}
                                    <div data-testid="edit-actions" className="flex flex-wrap items-center gap-x-2 gap-y-1.5 min-w-0 max-w-full">
                                        <button
                                            data-testid="edit-save-btn"
                                            onClick={handleConfirmEdit}
                                            disabled={!editContent.trim() || !editDirty}
                                            className="flex items-center gap-1.5 min-w-0 max-w-full px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                        >
                                            <Check size={14} className="shrink-0" />
                                            {/* #243: `min-w-0` lets the BUTTON shrink, but a bare
                                                text node still floors it at its longest word, so
                                                the row overflowed by exactly that excess at the
                                                narrow-split floor. The label must be able to
                                                truncate — the same idiom as the document cards. */}
                                            <span className="min-w-0 truncate">{t('msgSaveAndRegenerate', 'Save & Regenerate')}</span>
                                        </button>
                                        <button
                                            data-testid="edit-add-voice-btn"
                                            onClick={handleToggleEditRecording}
                                            disabled={voiceRecorder.transcribing}
                                            className={`flex items-center gap-1.5 min-w-0 max-w-full px-3 py-1.5 rounded-md text-sm disabled:opacity-50 ${
                                                voiceRecorder.isRecording
                                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                                    : 'border border-pai text-pai-secondary hover:bg-white/[0.07]'
                                            }`}
                                            title={voiceRecorder.isRecording ? t('msgStopAndAddRecording', 'Stop and add recording') : t('voiceRecord', 'Record voice')}
                                        >
                                            {voiceRecorder.transcribing ? (
                                                <><Loader2 size={14} className="animate-spin shrink-0" /> <span className="min-w-0 truncate">{t('voiceTranscribing', 'Transcribing…')}</span></>
                                            ) : voiceRecorder.isRecording ? (
                                                <><Square size={13} className="shrink-0" /> <span className="min-w-0 truncate">{t('msgStopAndAddElapsed', 'Stop & add ({n}s)', { n: voiceRecorder.elapsed })}</span></>
                                            ) : (
                                                <><Mic size={14} className="shrink-0" /> <span className="min-w-0 truncate">{t('msgAddVoice', 'Add voice')}</span></>
                                            )}
                                        </button>
                                        <button
                                            data-testid="edit-cancel-btn"
                                            onClick={handleCancelEdit}
                                            className="flex items-center gap-1.5 min-w-0 max-w-full ml-auto px-3 py-1.5 border border-pai rounded-md hover:bg-white/[0.07] text-sm text-pai-secondary"
                                        >
                                            <X size={14} className="shrink-0" />
                                            <span className="min-w-0 truncate">{t('commonCancel', 'Cancel')}</span>
                                        </button>
                                    </div>
                                    {/* Keyboard hints (#243) — kept, but demoted to a compact
                                        caption CLOSING the panel. The mockup omits them and
                                        instead leaves an empty block here; a single muted line
                                        fills that space with something useful, which is the
                                        PO's "fit together tightly and efficiently". */}
                                    <div data-testid="edit-hints" className="text-[11px] leading-snug text-pai-meta min-w-0 max-w-full break-words">
                                        {t('msgEditKeyboardHints', 'Enter to save, Shift+Enter for new line, Esc to cancel')}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : isUser && (message.voice || (Array.isArray(message.voiceSegments) && message.voiceSegments.length > 0)) ? (
                        /* Voice message bubble — with the typed-text half
                         * (if any) rendered above so it's visible to the
                         * user, not hidden inside the audio bubble. The
                         * `typedText` field is persisted in
                         * message_metadata.typed_text so it survives reload. */
                        <div className="relative">
                            {/* Elevated "Voice Message N Edited:" blocks (Final Edit #130):
                                any corrected transcription shows at the TOP of the turn,
                                above the players, styled like a typed-input block. */}
                            <EditedVoiceBlocks segments={message.voiceSegments} />
                            {message.typedText && (
                                <div className="mb-2 whitespace-pre-wrap break-words [overflow-wrap:anywhere] text-white">
                                    {message.typedText}
                                </div>
                            )}
                            <VoiceBubble message={message} renderedContent={renderedContent} />
                            {/* Edit control moved to the bubble action bar (Reply Arrow #141). */}
                        </div>
                    ) : (
                        /* Normal display mode */
                        <div className="relative">
                            {/* Error Clean up #439: a failed turn keeps whatever text
                                had already streamed and adds a clearly-marked error
                                notice below it. When the persisted content IS just
                                the error sentence (nothing streamed), the notice
                                alone is shown — never the sentence twice. */}
                            {!(streamError && (streamError.content_is_error || !(message.content || '').trim())) && renderedContent}
                            {stoppedInfo && (
                                <div
                                    data-testid="assistant-stopped-notice"
                                    data-stopped-by={stoppedInfo.stopped_by || ''}
                                    className={`flex items-center gap-2 text-[13px] text-pai-secondary min-w-0 ${renderedContent ? 'mt-2 pt-2 border-t border-pai-hairline' : ''}`}
                                >
                                    <Square size={12} className="shrink-0" />
                                    <span className="min-w-0 break-words">
                                        {stoppedInfo.stopped_by
                                            ? t('msgStoppedBy', 'PAI was stopped by {name}', { name: stoppedInfo.stopped_by })
                                            : t('msgStoppedByYou', 'PAI was stopped')}
                                    </span>
                                </div>
                            )}
                            {!isUser && streamError && (
                                <ChatErrorNotice
                                    streamError={streamError}
                                    onRetry={onRetry ? () => onRetry(message) : null}
                                    retryDisabled={retryDisabled}
                                />
                            )}
                            {/* Edit control moved to the bubble action bar (Reply Arrow #141). */}
                            {/* Edited indicator */}
                            {wasEdited && (
                                <span className="text-xs text-pai-faint ml-2">{t('msgEditedIndicator', '(edited)')}</span>
                            )}
                        </div>
                    )}
                    {/* Rich attached documents (persisted, survives refresh) */}
                    {(() => {
                        const docs = message.attachedDocuments || message.metadata?.attached_documents;
                        if (!docs || docs.length === 0) return null;
                        return (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {docs.map((att, idx) => (
                                    <MessageAttachment
                                        key={att.id || `att-${idx}`}
                                        attachment={att}
                                        onClick={(a) => handlePreviewDocument(a.id, a.filename)}
                                    />
                                ))}
                            </div>
                        );
                    })()}
                    {/* Legacy string-name attachments (pre-metadata persistence) */}
                    {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                            {message.attachments.map((file, index) => (
                                <AttachmentPill key={index} file={file} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Interactive transactions card list — emitted by the list_expenses tool */}
                {message.metadata?.expenses_listed && (
                    <ChatExpenseCardList
                        payload={message.metadata.expenses_listed}
                        onNavigate={(href) => navigate(href)}
                    />
                )}

                {/* Side-by-side duplicate warning — emitted by create_expense */}
                {message.metadata?.duplicate_warning && (
                    <DuplicateWarningCard
                        payload={message.metadata.duplicate_warning}
                        onNavigate={(href) => navigate(href)}
                        onPreview={handlePreviewDocument}
                        onDownload={handleDownloadDocument}
                    />
                )}

                {/* Intra-batch duplicate decision card — emitted by create_expense_batch */}
                {message.metadata?.batch_duplicate_summary && (
                    <BatchDuplicateCard
                        payload={message.metadata.batch_duplicate_summary}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* OCR failure — emitted by create_expense* when a receipt's amount
                    can't be read. Shows the image preview so the user can identify the
                    file; NO ledger row was created (ticket #57). */}
                {message.metadata?.expense_extraction_failed && (
                    <OcrFailedCard payload={message.metadata.expense_extraction_failed} />
                )}

                {/* Chat confirmation cards — all driven by the single config-driven
                    <ResolutionCard> engine (H-3.2). Each `config` (in
                    cards/cardConfigs.jsx) declares only what varies; the engine owns
                    the shared status/busy/error/removedIds/persist machine.
                    `cardProps` threads the Preview/Download/navigate handlers to the
                    rows that need them. */}
                {message.metadata?.document_delete_request && (
                    <ResolutionCard
                        config={documentDeleteConfig}
                        payload={message.metadata.document_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                        cardProps={{ onPreview: handlePreviewDocument, onDownload: handleDownloadDocument }}
                    />
                )}

                {message.metadata?.expense_delete_request && (
                    <ResolutionCard
                        config={expenseDeleteConfig}
                        payload={message.metadata.expense_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                        cardProps={{ onPreview: handlePreviewDocument, onDownload: handleDownloadDocument }}
                    />
                )}

                {message.metadata?.calendar_event_delete_request && (
                    <ResolutionCard
                        config={calendarDeleteConfig}
                        payload={message.metadata.calendar_event_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.calendar_events_delete_request && (
                    <ResolutionCard
                        config={calendarEventsDeleteConfig}
                        payload={message.metadata.calendar_events_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* Notes & Reminders (Fase 1) — delete confirmation cards. */}
                {message.metadata?.note_delete_request && (
                    <ResolutionCard
                        config={noteDeleteConfig}
                        payload={message.metadata.note_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* Task List (#437) — delete selected / completed / ALL tasks. */}
                {message.metadata?.task_delete_request && (
                    <ResolutionCard
                        config={taskDeleteConfig}
                        payload={message.metadata.task_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* PAI Shared notes (#259) — the mandatory share confirmation. */}
                {message.metadata?.note_share_request && (
                    <ResolutionCard
                        config={noteShareConfig}
                        payload={message.metadata.note_share_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.reminder_delete_request && (
                    <ResolutionCard
                        config={reminderDeleteConfig}
                        payload={message.metadata.reminder_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* Comment section PAI #242 — ticket-comment delete confirmation card. */}
                {message.metadata?.comment_delete_request && (
                    <ResolutionCard
                        config={commentDeleteConfig}
                        payload={message.metadata.comment_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.ticket_batch_update_request && (
                    <ResolutionCard
                        config={ticketBatchUpdateConfig}
                        payload={message.metadata.ticket_batch_update_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.ticket_batch_delete_request && (
                    <ResolutionCard
                        config={ticketBatchDeleteConfig}
                        payload={message.metadata.ticket_batch_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.board_batch_delete_request && (
                    <ResolutionCard
                        config={boardBatchDeleteConfig}
                        payload={message.metadata.board_batch_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                        cardProps={{ navigate }}
                    />
                )}

                {/* PAI board leaving (#275) — the mandatory exit confirmation card. */}
                {message.metadata?.board_leave_request && (
                    <ResolutionCard
                        config={boardLeaveConfig}
                        payload={message.metadata.board_leave_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.column_move_request && (
                    <ResolutionCard
                        config={columnMoveConfig}
                        payload={message.metadata.column_move_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.label_delete_request && (
                    <ResolutionCard
                        config={labelDeleteConfig}
                        payload={message.metadata.label_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.column_delete_request && (
                    <ResolutionCard
                        config={columnDeleteConfig}
                        payload={message.metadata.column_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.ticket_attach_request && (
                    <ResolutionCard
                        config={ticketAttachConfig}
                        payload={message.metadata.ticket_attach_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                        cardProps={{ onPreview: handlePreviewDocument, onDownload: handleDownloadDocument }}
                    />
                )}

                {message.metadata?.document_rename_request && (
                    <ResolutionCard
                        config={documentRenameConfig}
                        payload={message.metadata.document_rename_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.book_delete_request && (
                    <ResolutionCard
                        config={bookDeleteConfig}
                        payload={message.metadata.book_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* Finance book name change (#464) — current → new name, Confirm/Cancel. */}
                {message.metadata?.book_rename_request && (
                    <ResolutionCard
                        config={bookRenameConfig}
                        payload={message.metadata.book_rename_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* Rulebook in Telegram (#503) — edit_rulebook add/update/delete, Confirm/Cancel. */}
                {message.metadata?.rule_change_request && (
                    <ResolutionCard
                        config={ruleChangeConfig}
                        payload={message.metadata.rule_change_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* Contact creation — one card PER proposed contact (#515 Multiple
                    contacts in one message): map over the accumulated
                    `contact_creation_requests` array (fallback to the legacy single
                    `contact_creation_request` for older persisted messages). Each card
                    confirms / declines / edits on its own, resolved by its card_id. */}
                {/* #517: `cardEntriesForMessage` renders each card ONCE, in its final
                    state when one is recorded (a repeated entry collapses; a pending
                    entry whose scalar mirror holds the decision adopts it). */}
                {cardEntriesForMessage(message.metadata, 'contact_creation_request').map(({ payload, entryId, key }) => (
                    <ResolutionCard
                        key={key}
                        config={contactCreationConfig}
                        payload={payload}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                        entryId={entryId}
                    />
                ))}

                {/* Main Chat invite — create event + invite network/teams. #536: one
                    card PER invite (the `event_invite_requests` array, e.g. ≤5 events
                    with invitees), each resolved by its card_id; legacy scalar fallback. */}
                {cardEntriesForMessage(message.metadata, 'event_invite_request').map(({ payload, entryId, key }) => (
                    <ResolutionCard
                        key={key}
                        config={eventInviteConfig}
                        payload={payload}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                        entryId={entryId}
                    />
                ))}

                {/* Teams (Agenda TO Contacts — Phase C / T5) */}
                {message.metadata?.team_create_request && (
                    <ResolutionCard
                        config={teamCreateConfig}
                        payload={message.metadata.team_create_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.team_share_request && (
                    <ResolutionCard
                        config={teamShareConfig}
                        payload={message.metadata.team_share_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.team_delete_request && (
                    <ResolutionCard
                        config={teamDeleteConfig}
                        payload={message.metadata.team_delete_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* PAI-to-PAI direct send (Agenda TO Contacts — Phase D / T6 + T4) */}
                {message.metadata?.direct_send_request && (
                    <ResolutionCard
                        config={directSendConfig}
                        payload={message.metadata.direct_send_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {message.metadata?.action_revoke_request && (
                    <ResolutionCard
                        config={actionRevokeConfig}
                        payload={message.metadata.action_revoke_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                    />
                )}

                {/* PAI Rulebook #146 — the Assistant Desk's Add Rule card. Its
                    custom body needs the conversation/message ids to persist
                    the non-terminal `editing` status itself, and the engine
                    doesn't forward its own props into a customBody — so they
                    ride through cardProps, which the engine spreads into ctx. */}
                {message.metadata?.rule_add_request && (
                    <ResolutionCard
                        config={ruleAddConfig}
                        payload={message.metadata.rule_add_request}
                        conversationId={conversationId}
                        messageId={message.dbId || message.id}
                        cardProps={{ conversationId, messageId: message.dbId || message.id }}
                    />
                )}

                {/* #533 — more than 5 different events: ONE package card (numbered
                    rows, one Confirm / Cancel), rendered from the persisted metadata. */}
                {message.metadata?.calendar_event_package?.package_id && (
                    <div className="mt-2">
                        <CalendarPackageCard
                            key={message.metadata.calendar_event_package.package_id}
                            pkg={message.metadata.calendar_event_package}
                        />
                    </div>
                )}

                {/* #536 (PO 2026-11-02) — the GUEST answered some events of a numbered
                    invitation by text: the updated list of what is still pending,
                    Accept all / Decline all + the text-answer hint. */}
                {message.metadata?.event_invitation_package?.invitation_id && (
                    <div className="mt-2">
                        <GuestPackageInvitationCard
                            key={message.metadata.event_invitation_package.invitation_id}
                            invitation={message.metadata.event_invitation_package}
                        />
                    </div>
                )}

                {/* RAG Metadata */}
                {message.metadata && (message.metadata.has_rag_context || message.metadata.document_create || message.metadata.document_creates?.length || message.metadata.document_edits?.length || message.metadata.calendar_event_created || message.metadata.calendar_events_created?.length) && (
                    <div className="mt-2 space-y-2">
                        {/* Document Edit Info — one card PER edited document (Extra
                            Documents #187): a turn can edit many docs, so we map over the
                            accumulated `document_edits` array (falling back to the legacy
                            single `document_edit_complete` / metadata-chunk `document_edit`
                            for older persisted messages). */}
                        {(() => {
                            const editList = message.metadata.document_edits?.length
                                ? message.metadata.document_edits
                                : (message.metadata.document_edit_complete
                                    ? [message.metadata.document_edit_complete]
                                    : (message.metadata.document_edit ? [null] : []));
                            return editList.map((editComplete, editIdx) => {
                            const hasNewVersion = editComplete?.success && editComplete?.document_id;
                            const displayDocId = hasNewVersion ? editComplete.document_id : message.metadata.document_edit?.document_id;
                            const displayDocName = hasNewVersion ? editComplete.document_name : message.metadata.document_edit?.document_name;
                            const version = editComplete?.version;
                            return (
                                <div key={displayDocId || `edit-${editIdx}`} data-testid="document-edit-card" className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg text-xs min-w-0 max-w-full overflow-hidden">
                                    <div className="flex flex-wrap items-center gap-2 min-w-0 text-green-700 dark:text-green-300 font-medium mb-1">
                                        <span>✏️</span>
                                        <span>{version ? t('msgDocumentUpdatedVersion', 'Document updated (v{version})', { version }) : t('msgDocumentUpdated', 'Document updated')}</span>
                                    </div>
                                    <div className="text-green-600 dark:text-green-400 space-y-1 min-w-0">
                                        <div className="text-sm break-words [overflow-wrap:anywhere]">• {displayDocName}</div>
                                        {editComplete?.chunks_created && (
                                            <div className="text-sm">• {t('msgChunksIndexed', '{n} chunks indexed', { n: editComplete.chunks_created })}</div>
                                        )}
                                    </div>
                                    {displayDocId && (
                                        /* #243 2nd pass: the action row WRAPS and its buttons shrink —
                                           a fixed max-w-xs filename + a non-wrapping row forced this
                                           card ~430px wide, popping it out of the bubble/viewport. */
                                        <div className="mt-2 flex flex-wrap items-center gap-2 min-w-0">
                                            <button
                                                onClick={(e) => handleDocumentClick(e, displayDocId, displayDocName)}
                                                className="group relative px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 flex items-center gap-2 min-w-0 max-w-full bg-white/[0.04] border-[rgba(52,208,88,0.4)] text-pai-green hover:border-[rgba(52,208,88,0.7)] hover:bg-[rgba(52,208,88,0.09)] cursor-pointer"
                                                title={t('msgClickToViewDocument', 'Click to view document')}
                                            >
                                                <span className="text-base shrink-0">📄</span>
                                                <span className="min-w-0 truncate">{displayDocName}</span>
                                                <span className="text-xs shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">▼</span>
                                            </button>
                                            {version && version > 1 && (
                                                <button
                                                    onClick={() => setVersionHistoryDoc({ id: displayDocId, name: displayDocName })}
                                                    className="min-w-0 max-w-full px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 flex items-center gap-2 bg-white/[0.04] border-pai text-pai-secondary hover:border-pai-strong hover:bg-white/[0.08] cursor-pointer"
                                                    title={t('msgViewVersionHistory', 'View version history')}
                                                >
                                                    <span className="text-base shrink-0">🕘</span>
                                                    <span className="min-w-0 truncate">{t('msgHistory', 'History')}</span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                            });
                        })()}

                        {/* Document Create Info — one card PER created document (Extra
                            Documents #187): map over the accumulated `document_creates`
                            array (legacy fallback to the single fields). */}
                        {(() => {
                            const createList = message.metadata.document_creates?.length
                                ? message.metadata.document_creates
                                : (message.metadata.document_create_complete
                                    ? [message.metadata.document_create_complete]
                                    : (message.metadata.document_create ? [null] : []));
                            return createList.map((createComplete, createIdx) => {
                            const hasDoc = createComplete?.success && createComplete?.document_id;
                            const displayDocId = hasDoc ? createComplete.document_id : null;
                            const rawDocName = hasDoc ? createComplete.document_name : message.metadata.document_create?.filename;
                            const docStatus = displayDocId ? createdDocStatus[displayDocId] : null;
                            // `rawDocName` is already the LIVE name for a persisted
                            // message — GET /conversations/<id> hydrates every card
                            // event from the current document row (#330). `docStatus`
                            // only covers the window between a Manage save and the
                            // next fetch.
                            const effectiveDocName = docStatus?.newName || rawDocName;
                            // A card whose document no longer exists (trashed or
                            // hard-deleted) is reported by the BE, so it renders as
                            // gone instead of offering a dead link.
                            const isDiscarded = docStatus?.action === 'discarded' || createComplete?.document_deleted === true;
                            const isMoved = !!docStatus?.moved;
                            const isRenamed = !!docStatus?.renamed;
                            const effectiveFolderId = docStatus?.moved
                                ? (docStatus.newFolderId ?? null)
                                : createComplete?.folder_id;

                            if (isDiscarded) {
                                return (
                                    <div key={displayDocId || `create-${createIdx}`} className="p-2 bg-white/[0.06] border border-pai rounded-lg text-xs min-w-0 max-w-full">
                                        <div className="flex flex-wrap items-center gap-2 min-w-0 text-pai-meta font-medium">
                                            <span>🗑️</span>
                                            <span>{t('msgDocumentDiscarded', 'Document discarded')}</span>
                                        </div>
                                        <div className="text-pai-faint text-sm mt-1 line-through break-words [overflow-wrap:anywhere]">{rawDocName}</div>
                                    </div>
                                );
                            }

                            return (
                                <div key={displayDocId || `create-${createIdx}`} data-testid="document-create-card" className="p-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg text-xs min-w-0 max-w-full overflow-hidden">
                                    <div className="flex flex-wrap items-center gap-2 min-w-0 text-teal-700 dark:text-teal-300 font-medium mb-1">
                                        <span>📝</span>
                                        <span>{t('msgDocumentCreated', 'Document created')}</span>
                                        {isMoved && <span className="text-xs bg-teal-100 dark:bg-teal-800 px-1.5 py-0.5 rounded">{t('msgMovedBadge', 'Moved')}</span>}
                                        {isRenamed && <span className="text-xs bg-teal-100 dark:bg-teal-800 px-1.5 py-0.5 rounded">{t('msgRenamedBadge', 'Renamed')}</span>}
                                    </div>
                                    <div className="text-teal-600 dark:text-teal-400 space-y-1 min-w-0">
                                        <div className="text-sm break-words [overflow-wrap:anywhere]">• {effectiveDocName}</div>
                                        {createComplete?.chunks_created && (
                                            <div className="text-sm">• {t('msgChunksIndexed', '{n} chunks indexed', { n: createComplete.chunks_created })}</div>
                                        )}
                                    </div>
                                    {displayDocId && (
                                        /* #243 2nd pass: wrapping, shrinkable action row (see the edit card). */
                                        <div className="mt-2 flex flex-wrap items-center gap-2 min-w-0">
                                            <button
                                                onClick={(e) => handleDocumentClick(e, displayDocId, effectiveDocName)}
                                                className="group relative px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 flex items-center gap-2 min-w-0 max-w-full bg-white/[0.04] border-[rgba(52,208,88,0.32)] text-pai-green hover:border-[rgba(52,208,88,0.6)] hover:bg-[rgba(52,208,88,0.09)] cursor-pointer"
                                                title={t('msgClickToViewDocument', 'Click to view document')}
                                            >
                                                <span className="text-base shrink-0">📄</span>
                                                <span className="min-w-0 truncate">{effectiveDocName}</span>
                                                <span className="text-xs shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">▼</span>
                                            </button>
                                            <button
                                                data-testid="doc-card-manage"
                                                onClick={() => setCreatedDocModal({ id: displayDocId, name: effectiveDocName, folderId: effectiveFolderId })}
                                                className="min-w-0 max-w-full px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all duration-200 flex items-center gap-2 bg-white/[0.04] border-pai text-pai-secondary hover:border-pai-strong hover:bg-white/[0.08] cursor-pointer"
                                                title={t('msgRenameMoveDiscardTitle', 'Rename, move, or discard this document')}
                                            >
                                                <span className="text-base shrink-0">&#9881;</span>
                                                <span className="min-w-0 truncate">{t('msgManage', 'Manage')}</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                            });
                        })()}

                        {/* Calendar Event Booking Preview — one card PER created event
                            (Calendar Ui #226): a turn can create many events, so map over
                            the accumulated `calendar_events_created` array (fallback to the
                            legacy single `calendar_event_created` for older persisted
                            messages). Each card confirms/edits/declines independently; a
                            batch of 3+ stacks in a scrollable container. */}
                        {(() => {
                            const cal = message.metadata.calendar_events_created?.length
                                ? message.metadata.calendar_events_created
                                : (message.metadata.calendar_event_created ? [message.metadata.calendar_event_created] : []);
                            const shown = cal.filter((ev) => ev?.success);
                            if (!shown.length) return null;
                            return (
                                <div
                                    data-testid="calendar-cards-stack"
                                    className={`space-y-2 ${shown.length >= 3 ? 'max-h-96 overflow-y-auto pr-1' : ''}`}
                                >
                                    {shown.map((ev, i) => (
                                        <CalendarBookingCard
                                            key={ev.event_id || `cal-${i}`}
                                            event={ev}
                                            conversationId={conversationId}
                                            messageId={message.dbId || message.id}
                                        />
                                    ))}
                                </div>
                            );
                        })()}

                        {/* Document Context Info (non-edit RAG).
                            Gate on uniqueDocNames so we don't render an empty box when
                            the only "used" doc was one this message itself just created/edited. */}
                        {message.metadata.pipeline !== 'document_edit' && message.metadata.pipeline !== 'document_create' && message.metadata.context_chunks_used > 0 && uniqueDocNames.length > 0 && (
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg text-xs">
                                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 font-medium mb-1">
                                    <span>📄</span>
                                    <span>{t('msgEnhancedWithDocContext', 'Enhanced with document context')}</span>
                                </div>
                                <div className="text-blue-600 dark:text-blue-400 space-y-2">
                                    {documentSources.length > 0 ? (
                                        <>
                                            <div className="text-sm">• {t('msgUsingNDocuments', 'Using {n} document{s}', { n: uniqueDocNames.length, s: uniqueDocNames.length !== 1 ? 's' : '' })}</div>
                                            <div className="mt-2">
                                                <div className="font-medium text-blue-700 dark:text-blue-300 mb-2 text-sm">{t('msgDocumentsUsedLabel', 'Documents used:')}</div>
                                                <div className="flex flex-wrap gap-2">
                                                    {uniqueDocNames.map((docName, index) => {
                                                        // Find the source object with document_id
                                                        const source = documentSources.find(s => s.document_name === docName);
                                                        return (
                                                            <button
                                                                key={index}
                                                                onClick={(e) => source && source.document_id && handleDocumentClick(e, source.document_id, docName)}
                                                                disabled={!source || !source.document_id}
                                                                className={`
                                                                    group relative px-3 py-2 rounded-lg border-2 text-sm font-medium
                                                                    transition-all duration-200 flex items-center gap-2 min-w-0 max-w-full
                                                                    ${source && source.document_id
                                                                        ? 'bg-white/[0.04] border-[rgba(74,144,240,0.4)] text-pai-blue hover:border-[rgba(74,144,240,0.7)] hover:bg-[rgba(74,144,240,0.09)] cursor-pointer'
                                                                        : 'bg-white/[0.04] border-pai text-pai-faint cursor-not-allowed'
                                                                    }
                                                                `}
                                                                title={source && source.document_id ? t('msgClickToViewOptions', 'Click to view options') : t('msgDocumentNotAvailable', 'Document not available')}
                                                            >
                                                                <span className="text-base shrink-0">📄</span>
                                                                <span className="min-w-0 truncate">{docName}</span>
                                                                {source && source.document_id && (
                                                                    <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity">▼</span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-sm">• {t('msgUsingDocumentContext', 'Using document context')}</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Standard AI response (no document context, non-edit) */}
                        {message.metadata.pipeline !== 'document_edit' && message.metadata.context_chunks_used === 0 && (
                            <div className="p-2 bg-white/[0.05] border border-pai-subtle rounded-lg text-xs">
                                <div className="flex items-center gap-2 text-pai-secondary font-medium mb-1">
                                    <span>🤖</span>
                                    <span>{t('msgStandardAiResponse', 'Standard AI response')}</span>
                                </div>
                                <div className="text-pai-meta">
                                    • {t('msgNoDocumentContextUsed', 'No document context used')}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Grounding indicator — shown independently of RAG context */}
                {message.metadata?.grounding_used && (
                    <div className="p-2 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-700 rounded-lg text-xs mt-2">
                        <div className="flex items-center gap-2 text-cyan-700 dark:text-cyan-300 font-medium">
                            <span>🌐</span>
                            <span>{t('msgGoogleSearchUsed', 'Google Search was used to answer this query')}</span>
                            {message.metadata.grounding_search_count > 0 && (
                                <span className="text-cyan-500 dark:text-cyan-400">
                                    {t('msgSearchCount', '({n} search{es})', { n: message.metadata.grounding_search_count, es: message.metadata.grounding_search_count !== 1 ? 'es' : '' })}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* View Context Button + Save-as-document — assistant messages only */}
                {!isUser && (message.metadata?.full_prompt || message.dbId) && (
                    /* #243: the footer wraps AND its items shrink, so "View LLM Context" +
                       the download menu stay inside the bubble at minimum width. */
                    <div className="mt-2 flex items-center gap-3 flex-wrap min-w-0 max-w-full">
                        {/* #349: "View LLM Context" became "View Energy" — the
                            engineer's answer ("here is the raw prompt") gave way
                            to the owner's ("what did this cost me?"). The raw
                            trace is still one click away, inside the receipt. */}
                        <button
                            data-testid="msg-view-energy"
                            onClick={handleOpenEnergyReceipt}
                            className="pai-tap-row text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline flex items-center gap-1 min-w-0 max-w-full text-left"
                        >
                            <Zap size={12} className="shrink-0" />
                            <span className="min-w-0 break-words">{t('msgViewEnergy', 'View Energy')}</span>
                        </button>
                        {!isReadOnly && message.dbId && !stoppedInfo && (message.content || '').trim() && (
                            <DownloadReplyAsDocumentMenu message={message} />
                        )}
                    </div>
                )}

                <div className="text-xs text-pai-meta mt-2">
                    {new Date(message.timestamp).toLocaleTimeString()}
                </div>
            </div>

            {/* Document Action Menu */}
            {selectedDocument && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={handleCloseDocumentMenu}
                    />
                    {/* Menu */}
                    <div
                        className="fixed z-50 bg-pai-modal rounded-ctl shadow-modal-sm border border-pai py-1 min-w-[200px]"
                        style={{
                            top: `${documentMenuPosition.top}px`,
                            left: `${documentMenuPosition.left}px`
                        }}
                    >
                        {/* Menu Header */}
                        <div className="px-4 py-2 border-b border-pai-hairline">
                            <div className="flex items-center gap-2">
                                <span className="text-lg">📄</span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-white truncate">
                                        {selectedDocument.name}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Menu Items */}
                        <div className="py-1">
                            <button
                                onClick={() => handlePreviewDocument(selectedDocument.id, selectedDocument.name)}
                                className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors group"
                            >
                                <div className="text-sm font-medium text-white">{t('cardPreview', 'Preview')}</div>
                                <div className="text-xs text-pai-meta">{t('msgViewDocumentContent', 'View document content')}</div>
                            </button>

                            <button
                                onClick={() => handleDownloadDocument(selectedDocument.id, selectedDocument.name)}
                                className="w-full px-4 py-3 text-left hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors group"
                            >
                                <div className="text-sm font-medium text-white">{t('cardDownload', 'Download')}</div>
                                <div className="text-xs text-pai-meta">{t('msgSaveToYourDevice', 'Save to your device')}</div>
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Document Preview Modal */}
            {showPreviewModal && previewDocument && (
                <DocumentPreviewModal
                    documentId={previewDocument.id}
                    documentName={previewDocument.name}
                    onClose={() => {
                        setShowPreviewModal(false);
                        setPreviewDocument(null);
                    }}
                />
            )}

            {/* Version History Modal */}
            {versionHistoryDoc && (
                <VersionHistoryDialog
                    documentId={versionHistoryDoc.id}
                    documentName={versionHistoryDoc.name}
                    onClose={() => setVersionHistoryDoc(null)}
                    onPreview={(versionId, name) => {
                        setVersionHistoryDoc(null);
                        setPreviewDocument({ id: versionId, name });
                        setShowPreviewModal(true);
                    }}
                />
            )}

            {/* Energy Receipt (#349) — the primary "what happened here?" view.
                Only ever mounted for a PERSISTED assistant turn: the receipt is
                scoped to a stored message, and a still-streaming one has no id
                for the backend to bound a turn window with. */}
            {showEnergyReceipt && conversationId && message.dbId && (
                <EnergyReceiptModal
                    conversationId={conversationId}
                    messageId={message.dbId}
                    onClose={() => setShowEnergyReceipt(false)}
                    onOpenTechnical={handleOpenTechnical}
                />
            )}

            {/* Context Modal */}
            {showContextModal && (
                <ContextViewerModal
                    promptBreakdown={promptBreakdown}
                    fullPrompt={fullPrompt}
                    isLoading={loadingContext}
                    error={contextError}
                    onClose={() => setShowContextModal(false)}
                    groundingEnabled={message.metadata?.grounding_enabled}
                    groundingUsed={message.metadata?.grounding_used}
                    groundingSearchCount={message.metadata?.grounding_search_count}
                    retentionExpired={contextRetentionExpired}
                />
            )}

            {createdDocModal && (
                <CreatedDocumentModal
                    documentId={createdDocModal.id}
                    documentName={createdDocModal.name}
                    currentFolderId={createdDocModal.folderId}
                    onClose={() => setCreatedDocModal(null)}
                    onDocumentUpdated={handleDocumentUpdated}
                />
            )}
        </div>
    );
};

// Custom comparison to avoid re-renders when only irrelevant props change
const arePropsEqual = (prevProps, nextProps) => {
    // Always re-render if message content changed
    if (prevProps.message.content !== nextProps.message.content) return false;
    if (prevProps.message.isSearching !== nextProps.message.isSearching) return false;
    if (prevProps.message.toolSteps !== nextProps.message.toolSteps) return false;
    // Check if dbId was added (needed for edit functionality and reply scrolling)
    if (prevProps.message.dbId !== nextProps.message.dbId) return false;
    // Check if reply context was added/changed (for visual reply indicator)
    const prevReplied = prevProps.message.repliedTo;
    const nextReplied = nextProps.message.repliedTo;
    if ((prevReplied == null) !== (nextReplied == null)) return false;
    if (prevReplied?.content !== nextReplied?.content) return false;
    // Check if voice metadata changed
    if (prevProps.message.voice !== nextProps.message.voice) return false;
    if (prevProps.message.voiceSegments !== nextProps.message.voiceSegments) return false;
    if (prevProps.message.typedText !== nextProps.message.typedText) return false;
    // Check if attached documents changed
    if (prevProps.message.attachedDocuments !== nextProps.message.attachedDocuments) return false;

    // Check if highlight status changed for THIS message
    if (prevProps.isHighlighted !== nextProps.isHighlighted) return false;

    // Check basic props
    if (prevProps.isUser !== nextProps.isUser) return false;

    // Re-render when the user's avatar initials resolve (auth loads async).
    if (prevProps.userInitials !== nextProps.userInitials) return false;

    // Check if this message's "last user message" status changed (for edit button visibility)
    if (prevProps.isLastUserMessage !== nextProps.isLastUserMessage) return false;
    if (prevProps.onReply !== nextProps.onReply) return false;
    // F-5.1: reply-selection is now a primitive prop (was an uncompared Set) — MUST
    // be compared or toggling reply-selection wouldn't re-highlight the bubble.
    if (prevProps.isSelectedForReply !== nextProps.isSelectedForReply) return false;

    // Re-render when the search TERM changes (which marks exist). Moving between
    // matches deliberately does not re-render: `useInChatSearch` styles the active
    // mark imperatively (#352).
    if (prevProps.searchQuery !== nextProps.searchQuery) return false;

    // #439: the error notice (metadata.stream_error) + its Retry wiring.
    if (prevProps.message.metadata?.stream_error !== nextProps.message.metadata?.stream_error) return false;
    // #457: the stopped notice (and the partial text it carries).
    if (prevProps.message.metadata?.stopped !== nextProps.message.metadata?.stopped) return false;
    if (prevProps.message.metadata?.partial_text !== nextProps.message.metadata?.partial_text) return false;
    if (prevProps.onRetry !== nextProps.onRetry) return false;
    if (prevProps.retryDisabled !== nextProps.retryDisabled) return false;

    // For metadata, only check if has_rag_context changed (not full_prompt)
    const prevHasRag = prevProps.message.metadata?.has_rag_context;
    const nextHasRag = nextProps.message.metadata?.has_rag_context;
    if (prevHasRag !== nextHasRag) return false;

    // Props are equal enough - skip re-render
    return true;
};

export default memo(Message, arePropsEqual);